/**
 * NegocioDetailView -- Negocio detail with tabs: Info, Contacto, Equipos, Suscripcion.
 * Supports inline edit (PUT) and delete for the current user role.
 *
 * @module presentation/views/NegocioDetailView
 */

import { BusinessApiClient }    from '../../infrastructure/api/BusinessApiClient.js';
import { fetchQueCambia } from '../../infrastructure/api/ApiBase.js';
import { SubscriptionApiClient } from '../../infrastructure/api/SubscriptionApiClient.js';
import { EquiposApiClient }       from '../../infrastructure/api/EquiposApiClient.js';
import { propietarioDe, verificadoDe, origenDe, consentimientoDelNegocio, resumenDeCasillas, cuerpoDeConsentimiento, motivoDeMarca } from '../../application/equipos/casillasEquipo.js';
import { EquiposCatalogApiClient } from '../../infrastructure/api/EquiposCatalogApiClient.js';
import { avisoDeMaqueta, accionDeMaqueta } from '../../application/equipos/maqueta.js';
import { filasDeLaRonda } from '../../application/negocios/rondaDelNegocio.js';
import { leerMarca, cuerpoDeMarca, loQueFalta, motivoDeLaMarcaDelNegocio, ETIQUETAS, EJEMPLOS } from '../../application/negocios/marcaDelNegocio.js';
import { LicenseApiClient }       from '../../infrastructure/api/LicenseApiClient.js';
import { leerCatalogo, opcionesConValorActual, MENSAJE_SIN_CATALOGO } from '../../application/negocios/catalogo.js';
import { idDelNegocio, porQueNoHayInvitacion, tokenDeLaInvitacion, puedeInvitar, INVITACION_LA_MANDA_NEXO } from '../../application/negocios/idDelNegocio.js';
import { AppConfig } from '../../config.js';
import { ciudadesDe, departamentosCon } from '../../application/negocios/colombia.js';
import { estaArchivado, insigniaDeEstado, cuerpoDeArchivar, leerRespuestaDeArchivo } from '../../application/negocios/archivoNegocios.js';
import { AparienciaApiClient }   from '../../infrastructure/api/AparienciaApiClient.js';
import { SALON, piezasQueTapan, resumenDelSalon } from '../../application/negocios/marcoDelSalon.js';
import { fondoParaGuardar, transparenciaDelLogo, avisoDeTransparencia, medidasDeLaImagen, avisoDeProporcion, porQueNoSePuedeSubir } from '../../application/negocios/archivoDeApariencia.js';
import { rutaPersonasDelNegocio } from '../../application/personas/listaMaster.js';
import { avisar, confirmar, pedirTexto, mostrarSecreto } from '../shared/nxDialogo.js';
import {
  saveFondoWebhookPayload,
  loadFondoWebhookPayload,
  clearFondoWebhookPayload,
  isFondoCacheEntryFresh,
} from '../../infrastructure/fondoResultCache.js';

const LICENSE_LABELS = { active: 'activa', trial: 'prueba', expired: 'expirada', suspended: 'suspendida' };
const LICENSE_COLORS = { active: '#238636', trial: '#d29922', expired: '#f85149', suspended: '#6e7681' };

const TABS     = ['info', 'contacto', 'propietario', 'equipos', 'instalaciones', 'suscripcion', 'apariencia'];
const TAB_NAMES = { info: 'Info', contacto: 'Contacto', propietario: 'Propietario', equipos: 'Equipos', instalaciones: 'Instalaciones', suscripcion: 'Suscripción', apariencia: 'Apariencia' };

// PANEL-OPS-V3-001 T4 — limites de instalaciones por plan
const INSTALL_LIMITS = { basico: 1, plus: 2, pro: 3, 'pro marketing': 3, enterprise: 10 };
function planInstallLimit(plan) {
  const key = String(plan || '').trim().toLowerCase();
  if (key in INSTALL_LIMITS) return INSTALL_LIMITS[key];
  if (key.includes('enter')) return 10;
  if (key.includes('pro')) return 3;
  if (key.includes('plus')) return 2;
  return 1;
}


let businessId   = null;
let currentBiz   = null;
let currentTab   = 'info';
let isSaving     = false;
let delegationBound = false;
// AbortController para limpiar listeners de delegation en cada re-render.
// Antes: cada render llamaba bindDelegation() que hacia addEventListener
// sin removeEventListener, resultando en multiples listeners acumulados
// → click en "+ Nueva" creaba 3+ suscripciones por 1 click.
let delegationAbortCtrl = null;
// TIPO-NEGOCIO-CANON-T1: tipos y planes salen del config (la canon en la base).
// Antes vivian escritos aqui y en otras tres vistas, y no coincidian.
let _catalogo    = leerCatalogo(null);

// Deuda 4: este escape (no cambiaba la comilla simple) es ahora el compartido.
import { esc } from '../shared/texto.js';
import { blobDeUrl } from '../../infrastructure/descargarArchivo.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

/** Evita mostrar cadenas base64 enormes en la UI */
function isDataImageUrl(s) {
  return typeof s === 'string' && /^data:image\//i.test(s);
}

/** n8n a veces envía error: [null]; no debe considerarse fallo de variante. */
function normalizeWebhookError(val) {
  if (val == null || val === '') return null;
  if (Array.isArray(val)) {
    const first = val.find(x => x != null && x !== '');
    if (first == null || first === '') return null;
    return typeof first === 'string' ? first : String(first);
  }
  if (typeof val === 'string' && val.trim()) return val;
  if (typeof val === 'object' && val.message) return String(val.message);
  return null;
}

function shortenUrlCaption(url, maxLen = 72) {
  if (!url || typeof url !== 'string') return '';
  if (isDataImageUrl(url)) {
    return 'Imagen incrustada (guardada en la cuenta del negocio en la base de datos; no es un archivo en Dropbox).';
  }
  if (url.length <= maxLen) return url;
  return `${url.slice(0, maxLen)}…`;
}

/** Referencias cortas para previews base64 (evita data-src de megabytes en el HTML) */
const fondoPreviewRefStore = new Map();
let fondoPreviewSeq = 0;

function revokeFondoPreviewBlobs() {
  fondoPreviewRefStore.forEach((v) => {
    if (v.blobUrl) URL.revokeObjectURL(v.blobUrl);
  });
  fondoPreviewRefStore.clear();
}

/** @returns {{ refId: string, previewSrc: string }} */
function registerFondoB64Src(dataUrl) {
  const id = `nxfp_${++fondoPreviewSeq}`;
  try {
    const parts = dataUrl.split(',');
    const header  = parts[0] || '';
    const b64data = parts[1] || '';
    const mimeMatch = /^data:([^;]+);/.exec(header);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const binary = atob(b64data);
    const bytes  = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blobUrl = URL.createObjectURL(new Blob([bytes], { type: mime }));
    fondoPreviewRefStore.set(id, { dataUrl, blobUrl });
    return { refId: id, previewSrc: blobUrl };
  } catch {
    fondoPreviewRefStore.set(id, { dataUrl, blobUrl: null });
    return { refId: id, previewSrc: dataUrl };
  }
}

function resolveFondoSrcFromBtn(btn) {
  const ref = btn.dataset.fondoRef;
  if (ref && fondoPreviewRefStore.has(ref)) return fondoPreviewRefStore.get(ref).dataUrl;
  return btn.dataset.src || '';
}

function licenseBadge(raw) {
  const key = raw === true ? 'active' : raw === false ? 'expired' : String(raw ?? '').toLowerCase();
  const color = LICENSE_COLORS[key] || '#6e7681';
  const label = LICENSE_LABELS[key] || 'sin licencia';
  return `<span class="status-badge" style="background:${color}22;color:${color}">${label}</span>`;
}

// NEGOCIOS-ARCHIVAR-VISTA-T1: la misma insignia que la lista (incluye pendiente y archivado, que el enum ya trae).
function bizStatusBadge(status) {
  const { etiqueta, color } = insigniaDeEstado({ status });
  return `<span class="status-badge" data-estado-negocio style="background:${color}22;color:${color}">${etiqueta}</span>`;
}

/**
 * Pide el motivo de archivar en un modal propio (nunca prompt). Resuelve con el texto o null si cancelan.
 * aviso: lo que dijo la API o la regla en la vuelta anterior; confirmando: la suscripcion vigente ya se aviso.
 */
function pedirMotivoDeArchivo(nombre, aviso = '', confirmando = false) {
  return new Promise((resolve) => {
    document.getElementById('nx-archivar-modal')?.remove();
    const ov = document.createElement('div');
    ov.id = 'nx-archivar-modal';
    ov.style.cssText = 'position:fixed;inset:0;z-index:9999;background:rgba(0,0,0,0.6);display:flex;align-items:center;justify-content:center;padding:16px';
    ov.innerHTML = `
      <div role="dialog" aria-modal="true" aria-labelledby="nx-archivar-titulo" style="background:#161b22;border:1px solid #30363d;border-radius:10px;max-width:420px;width:100%;padding:20px">
        <h3 id="nx-archivar-titulo" style="margin:0 0 6px;font-size:1rem">Archivar ${esc(nombre || 'negocio')}</h3>
        <p style="margin:0 0 10px;font-size:0.875rem;color:#8b949e">Sale de las listas, se apagan sus equipos y se cancela su suscripcion. Sus ventas, pagos y equipos se conservan; se puede desarchivar.</p>
        <label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem;color:#8b949e">Motivo *
          <input id="nx-archivar-motivo" type="text" class="form-input" placeholder="Ej: ya no trabaja con Nexo">
        </label>
        <p id="nx-archivar-aviso" style="margin:8px 0 0;font-size:0.875rem;color:${confirmando ? '#d29922' : '#f85149'};min-height:16px">${esc(aviso)}</p>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:12px">
          <button type="button" class="btn-ghost btn-sm" id="nx-archivar-cancelar">Cancelar</button>
          <button type="button" class="btn-primary btn-sm" id="nx-archivar-ok">${confirmando ? 'Archivar de todas formas' : 'Archivar'}</button>
        </div>
      </div>`;
    document.body.appendChild(ov);
    const $m = ov.querySelector('#nx-archivar-motivo');
    if (pedirMotivoDeArchivo.ultimo) $m.value = pedirMotivoDeArchivo.ultimo;
    const cerrar = (v) => { if (v !== null) pedirMotivoDeArchivo.ultimo = v; ov.remove(); resolve(v); };
    ov.querySelector('#nx-archivar-cancelar').addEventListener('click', () => { pedirMotivoDeArchivo.ultimo = ''; cerrar(null); });
    ov.addEventListener('click', (ev) => { if (ev.target === ov) { pedirMotivoDeArchivo.ultimo = ''; cerrar(null); } });
    ov.querySelector('#nx-archivar-ok').addEventListener('click', () => cerrar($m.value));
    $m.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') cerrar($m.value); });
    $m.focus();
  });
}

function fieldRO(label, value, placeholder = '—') {
  return `
    <div class="detail-field">
      <span class="detail-label">${label}</span>
      <span class="detail-value">${value || placeholder}</span>
    </div>`;
}

function fieldInput(id, label, value, type = 'text', placeholder = '') {
  return `
    <div class="detail-field">
      <label class="detail-label" for="${id}">${label}</label>
      <input type="${type}" id="${id}" class="form-input" value="${esc(value)}" placeholder="${esc(placeholder)}">
    </div>`;
}

function fieldSelect(id, label, value, options) {
  const opts = options.map(o =>
    `<option value="${esc(o)}"${o === value ? ' selected' : ''}>${esc(o)}</option>`
  ).join('');
  return `
    <div class="detail-field">
      <label class="detail-label" for="${id}">${label}</label>
      <select id="${id}" class="form-input">${opts}</select>
    </div>`;
}

function fmt(dateStr) {
  if (!dateStr) return '—';
  // Usar T12:00 para evitar off-by-one al convertir UTC→local en zonas negativas
  const d = dateStr.length === 10 ? new Date(dateStr + 'T12:00:00') : new Date(dateStr);
  return formatearFecha(d, { day: '2-digit', month: 'short', year: 'numeric' });
}
function isoToDate(dateStr) {
  if (!dateStr) return '';
  try {
    // Preservar la fecha sin offset UTC→local
    const d = dateStr.length === 10 ? new Date(dateStr + 'T12:00:00') : new Date(dateStr);
    return d.toISOString().slice(0, 10);
  } catch { return ''; }
}
/** Convierte string YYYY-MM-DD de input date a ISO sin perder el dia por timezone */
function dateInputToISO(dateStr) {
  if (!dateStr) return undefined;
  return dateStr + 'T12:00:00.000Z';
}

// PANEL-WIZARD-SUBS-FECHA-PASADA-001: valida las fechas de una suscripcion antes de
// persistir. Mismo protocolo Nexo que el wizard de negocio nuevo: el vencimiento no puede
// ser anterior al periodo de gracia (5 dias atras) ni anterior a la fecha de inicio. El
// fin es opcional (sub sin vencimiento). Recibe strings de <input type="date"> (YYYY-MM-DD).
// Devuelve mensaje de error o null si es valida.
function validateSubDates(startStr, endStr) {
  if (!endStr) return null; // fin opcional
  const end = new Date(endStr + 'T12:00:00');
  if (isNaN(end.getTime())) return 'Fecha de vencimiento inválida';
  const limiteGracia = new Date();
  limiteGracia.setHours(12, 0, 0, 0);
  limiteGracia.setDate(limiteGracia.getDate() - 5);
  if (end < limiteGracia) return 'El vencimiento no puede ser anterior al período de gracia (máx. 5 días atrás).';
  if (startStr) {
    const start = new Date(startStr + 'T12:00:00');
    if (!isNaN(start.getTime()) && end < start) return 'El vencimiento no puede ser anterior a la fecha de inicio.';
  }
  return null;
}

// PANEL-WIZARD-PARIDAD-NEGOCIO-001: deriva el modo de vencimiento de las notas que
// escribe el wizard ('vencimiento exacto' vs '(N meses)'). Best-effort panel: si la sub
// no fue creada por wizard o las notas no tienen el marcador, devuelve null (sin chip).
function subVencMode(notes) {
  const n = String(notes || '').toLowerCase();
  if (/exacto/.test(n)) {
    return { label: 'Exacto', color: '#d29922', title: 'Vencimiento fijado como fecha exacta (se acepta hasta 5 días atrás por período de gracia).' };
  }
  if (/\d+\s*mes/.test(n)) {
    return { label: 'Calculado', color: '#58a6ff', title: 'Vencimiento calculado automáticamente por duración en meses.' };
  }
  return null;
}

// ONB-COLOMBIA-UNICA: la lista sale de colombia.js. departamentosCon y ciudadesDe ponen un vacio
// cuando no hay valor y conservan el guardado aunque no este en la lista: un <select> sin opcion
// marcada muestra la primera y al guardar la escribe (Amazonas / Medellin sin que nadie las elija).
function opcionesDelSelect(lista, valor) {
  const v = String(valor ?? '').trim();
  return lista.map(x =>
    `<option value="${esc(x)}"${x === v ? ' selected' : ''}>${x ? esc(x) : '— Seleccionar —'}</option>`
  ).join('');
}

function fieldDeptSelect(id, label, value) {
  const opts = opcionesDelSelect(departamentosCon(value), value);
  return `
    <div class="detail-field">
      <label class="detail-label" for="${id}">${label}</label>
      <select id="${id}" class="form-input" data-dept-select>${opts}</select>
    </div>`;
}

function fieldCitySelect(id, label, dept, cityValue) {
  if (!ciudadesDe(dept).length) {
    return fieldInput(id, label, cityValue);
  }
  const cities = ciudadesDe(dept, cityValue);
  const opts = opcionesDelSelect(String(cityValue ?? '').trim() ? cities : ['', ...cities], cityValue);
  return `
    <div class="detail-field">
      <label class="detail-label" for="${id}">${label}</label>
      <select id="${id}" class="form-input">${opts}</select>
    </div>`;
}

/** Extrae valor de campo de metadata, manejando valores que sean objetos (JObject → string)
 *  PANEL-WIZARD-PARIDAD-NEGOCIO-001 [REDIRECT bit 851]: defensivo ante metadata corrupted
 *  con forma { "ValueKind": N } (bug historico backend al serializar JsonElement con
 *  Newtonsoft). El fix backend impide nuevos casos pero negocios viejos pueden traer este
 *  dato — los tratamos como vacios (Fenix re-edita en tab Propietario). */
function metaStr(b, key, fallback = '') {
  const v = b.metadata?.[key];
  if (v == null) return fallback;
  if (typeof v === 'boolean' || typeof v === 'number') return String(v);
  if (typeof v === 'string') {
    // Detectar string corrupted '{"ValueKind":3}' u objeto serializado JsonElement
    if (/^\s*\{\s*"ValueKind"\s*:/.test(v)) return fallback;
    return v;
  }
  if (typeof v === 'object') {
    // Objeto plano corrupted { ValueKind: N } → vacio
    if ('ValueKind' in v && Object.keys(v).length <= 2) return fallback;
    return v.value ?? v.stringValue ?? v.text ?? v.name ?? JSON.stringify(v);
  }
  return String(v);
}

function meta(b, key, fallback = '') {
  return metaStr(b, key, fallback);
}

function docBadge(url) {
  if (!url) return '<span style="color:#6e7681;font-size:0.875rem">Sin archivo</span>';
  return `<a href="${esc(url)}" target="_blank" class="detail-link" style="font-size:0.875rem">Ver documento</a>`;
}

export async function renderNegocioDetail(container) {
  businessId      = window.location.hash.split('/').pop();
  currentTab      = 'info';
  isSaving        = false;
  delegationBound = false;  // siempre re-bind al nuevo container
  _equiposCache    = [];
  _equiposFilters  = { search: '', tipo: '', estado: '' };

  container.innerHTML = '<p class="empty-state">Cargando negocio...</p>';

  const [result, configResult] = await Promise.all([
    BusinessApiClient.getById(businessId),
    BusinessApiClient.getConfig()
  ]);

  _catalogo = leerCatalogo(configResult);

  if (!result.ok) {
    container.innerHTML = `
      <section class="artifact-view">
        ${regresoHtml({ volverA: "#/negocios", texto: "Negocios" })}
        <p class="empty-state">Error: ${result.error}</p>
      </section>`;
    return;
  }

  currentBiz = result.data?.data || result.data;

  // Registrar handler global para invitación de onboarding.
  // ONB-T7 (2026-07-24): antes armaba el link a mano con #/onboarding/{bizId}
  // — ruta inexistente, exponía el business_id, y NUNCA llamaba al backend.
  // Ahora pide el token real a POST /onboarding/invitations y arma el link
  // público correcto (#/onboard/{token}).
  window.__nxSendOnboarding = async (bizId, bizName) => {
    // ONB-BOTON-INVITACION-T1: el id llegaba vacio (la ficha trae business_id y aqui se leia
    // b.id), asi que el API respondia 400 y en pantalla salia un "no se pudo" sin causa.
    if (!bizId) {
      showStatus('La ficha no trae el identificador del negocio: recarga la pagina e intenta otra vez.', true);
      return;
    }
    const inv = await BusinessApiClient.createOnboardingInvitation(bizId);
    // ONB-BOTON-INVITACION-T2: el token viene dentro del sobre del API (data.data.token).
    const token = tokenDeLaInvitacion(inv);
    if (!token) {
      showStatus(`No se pudo generar la invitación de registro. ${porQueNoHayInvitacion(inv)}`, true);
      return;
    }
    const onboardingUrl = `${window.location.origin}${window.location.pathname}#/onboard/${token}`;
    const msg = `Hola! Te invitamos a registrarte en Nexo Digital 360.\n\n`
              + `Negocio: ${bizName}\n`
              + `Completa tu registro aquí:\n${onboardingUrl}\n\n`
              + `Nuestro asistente Argemiro te guiará paso a paso.`;

    // ONB-FICHA-MOVIL-T1: el enlace se MUESTRA en la ficha. Antes se copiaba al portapapeles
    // y se abria WhatsApp con window.open DESPUES del await: en el celular el navegador
    // bloquea esa ventana porque ya no es un clic del usuario, y Josse se quedaba sin nada
    // en pantalla. Ahora quedan tres enlaces de verdad (Abrir, Copiar, Enviar por WhatsApp)
    // y cada uno se activa con SU clic.
    pintarInvitacion(onboardingUrl, msg);
  };

  // Handler: cargar usuarios panel_auth del negocio
  window.__nxLoadBizCredentials = async (bizId) => {
    const $body = document.getElementById('nx-biz-credentials-body');
    if (!$body) return;
    $body.innerHTML = '<span style="color:#8b949e">Cargando...</span>';
    const res = await BusinessApiClient.getPanelUsers(bizId);
    if (!res.ok) {
      $body.innerHTML = `<span style="color:#f85149">Error: ${esc(res.error || 'no se pudo cargar')}</span>`;
      return;
    }
    const { users, count } = res.data || {};
    if (!count) {
      $body.innerHTML = `
        <div style="color:#8b949e">
          <strong>Sin usuarios panel</strong><br>
          Este negocio no tiene un usuario panel_auth asociado todavia.
          <div style="margin-top:10px">
            <button class="btn-primary btn-sm" style="padding:6px 14px;font-size:14px;cursor:pointer"
                    onclick="window.__nxCreateBizUser && window.__nxCreateBizUser('${esc(bizId)}')">
              + Crear usuario panel
            </button>
            <span style="margin-left:8px;font-size:0.875rem;color:#8b949e">
              Genera email default y contrasena temporal
            </span>
          </div>
        </div>`;
      return;
    }
    $body.innerHTML = users.map(u => `
      <div style="padding:10px;border:1px solid #21262d;border-radius:6px;margin-bottom:8px;background:#161b22">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px;flex-wrap:wrap;gap:6px">
          <div>
            <code style="font-size:.9rem;color:#c9d1d9">${esc(u.email)}</code>
            <span style="margin-left:8px;padding:2px 8px;background:#1f6feb33;border-radius:10px;font-size:0.875rem;color:#58a6ff">${esc(u.role || 'member')}</span>
          </div>
          <div style="display:flex;gap:6px">
            <button class="btn-ghost btn-sm" style="padding:3px 10px;font-size:14px;cursor:pointer"
                    onclick="navigator.clipboard.writeText('${esc(u.email)}').then(()=>{this.textContent='Copiado!';setTimeout(()=>this.textContent='Copiar email',1500)})">Copiar email</button>
            <button class="btn-ghost btn-sm" style="padding:3px 10px;font-size:14px;cursor:pointer;color:#d29922"
                    title="Genera una contrasena nueva y la muestra una vez. La anterior queda invalidada."
                    onclick="window.__nxResetBizPassword && window.__nxResetBizPassword('${esc(bizId)}','${esc(u.email)}')">🔑 Ver nueva contrasena</button>
          </div>
        </div>
        ${u.displayName ? `<div style="font-size:0.875rem;color:#8b949e">${esc(u.displayName)}</div>` : ''}
      </div>
    `).join('') + `
      <div style="margin-top:8px;padding:8px 10px;background:#161b22;border:1px dashed #30363d;border-radius:4px;font-size:0.875rem;color:#8b949e;line-height:1.5">
        💡 Si olvidaste la contrasena, usa <strong style="color:#d29922">Ver nueva contrasena</strong>.
        La contrasena solo se muestra una vez al crearla/regenerarla por seguridad (la BD solo guarda hash).
      </div>
    `;
  };

  // PANEL-OPS-V2-001 T3 — Configuracion operativa por negocio
  window.__nxLoadOpsConfig = async (bizId) => {
    const $body = document.getElementById('nx-ops-config-body');
    if (!$body) return;
    $body.innerHTML = '<span style="color:#8b949e">Cargando...</span>';
    const res = await BusinessApiClient.getOpsConfig(bizId);
    if (!res.ok) {
      // [NEEDS-API] fallback — endpoint existente pero puede devolver 404 si aun no hay row
      $body.innerHTML = `
        <div style="color:#d29922;font-size:0.875rem;margin-bottom:8px">
          No se pudo cargar la config del servidor (${esc(res.error || 'error')}). Puedes configurarla y guardar.
        </div>
        ${__nxBuildOpsConfigForm(bizId, { asistencia_strict_mode: false, umbral_tardanza_minutos: 5, periodo_gracia_dias: 7 })}`;
      __nxBindOpsConfigForm(bizId);
      return;
    }
    const cfg = res.data?.data || res.data || {};
    const normalized = {
      asistencia_strict_mode: !!cfg.asistencia_strict_mode,
      umbral_tardanza_minutos: Number(cfg.umbral_tardanza_minutos ?? 5),
      periodo_gracia_dias: Number(cfg.periodo_gracia_dias ?? 7)
    };
    $body.innerHTML = __nxBuildOpsConfigForm(bizId, normalized);
    __nxBindOpsConfigForm(bizId);
  };

  window.__nxSaveOpsConfig = async (bizId) => {
    const strict = !!document.getElementById('nx-ops-strict')?.checked;
    const umbral = parseInt(document.getElementById('nx-ops-umbral')?.value || '5', 10);
    const gracia = parseInt(document.getElementById('nx-ops-gracia')?.value || '7', 10);
    const $status = document.getElementById('nx-ops-save-status');
    if ($status) { $status.textContent = 'Guardando...'; $status.style.color = '#8b949e'; }
    const res = await BusinessApiClient.updateOpsConfig(bizId, {
      asistencia_strict_mode: strict,
      umbral_tardanza_minutos: Number.isFinite(umbral) ? umbral : 5,
      periodo_gracia_dias: Number.isFinite(gracia) ? gracia : 7
    });
    if ($status) {
      if (res.ok) {
        $status.textContent = 'Guardado';
        $status.style.color = '#3fb950';
        // Cache en sesion para que AsistenciaView consuma inmediato sin roundtrip
        window.nxBusinessConfig = {
          ...(window.nxBusinessConfig || {}),
          asistencia_strict_mode: strict,
          umbral_tardanza_minutos: umbral,
          periodo_gracia_dias: gracia
        };
      } else {
        $status.textContent = `Error: ${res.error || 'no se pudo guardar'}`;
        $status.style.color = '#f85149';
      }
      setTimeout(() => { if ($status) $status.textContent = ''; }, 3500);
    }
  };

  // Handler: crear usuario panel_auth para el negocio (primer usuario)
  window.__nxCreateBizUser = async (bizId) => {
    // Pedir email y role con defaults (vacio = el correo por defecto). Deuda 1 (#32644): en la pagina, no con el dialogo del navegador.
    const emailInput = await pedirTexto({
      titulo: 'Crear usuario del panel',
      texto: 'Con este correo se inicia sesion en el panel y en Nexo Desktop.\nSi lo dejas vacio se usa el correo por defecto del negocio.',
      etiqueta: 'Correo del usuario'
    });
    // Si cancelo (null) abortar; si string vacio se usa default
    if (emailInput === null) return;

    const roleInput = await pedirTexto({
      titulo: 'Rol del usuario',
      etiqueta: 'Rol',
      valor: 'manager',
      aceptar: 'Crear usuario',
      opciones: [
        { valor: 'manager', texto: 'Usuario principal del negocio (manager)' },
        { valor: 'admin', texto: 'Admin de Nexo (admin)' },
        { valor: 'client', texto: 'Cliente final (client)' },
        { valor: 'member', texto: 'Miembro del equipo (member)' }
      ]
    });
    if (roleInput === null) return;

    const body = {};
    if (emailInput && emailInput.trim()) body.email = emailInput.trim();
    if (roleInput && roleInput.trim()) body.role = roleInput.trim();

    const res = await BusinessApiClient.createPanelUser(bizId, body);
    if (!res.ok) {
      avisar(`No se pudo crear el usuario: ${res.error || 'intenta de nuevo'}`, { tono: 'error' });
      return;
    }
    const { email, password, displayName, role } = res.data || {};
    await mostrarSecreto({
      titulo: 'Usuario creado',
      texto: `Correo: ${email}\nRol: ${role}\nNombre: ${displayName || '(sin nombre)'}\nGuarda la contrasena ahora: no se volvera a mostrar.`,
      secreto: password,
      copiar: `Email: ${email}\nContrasena: ${password}`
    });
    // Refrescar lista
    window.__nxLoadBizCredentials && window.__nxLoadBizCredentials(bizId);
  };

  // Handler: regenerar contrasena de un usuario especifico
  window.__nxResetBizPassword = async (bizId, email) => {
    const seguir = await confirmar({
      titulo: `Regenerar la contrasena de ${email}`,
      texto: 'La contrasena anterior deja de servir.\nLa nueva se muestra UNA SOLA VEZ.',
      aceptar: 'Regenerar contrasena',
      peligro: true
    });
    if (!seguir) return;
    const res = await BusinessApiClient.resetPanelPassword(bizId, email);
    if (!res.ok) {
      avisar(`No se pudo regenerar la contrasena: ${res.error || 'intenta de nuevo'}`, { tono: 'error' });
      return;
    }
    const { password } = res.data || {};
    await mostrarSecreto({
      titulo: `Nueva contrasena para ${email}`,
      texto: 'Copiala ahora: no se volvera a mostrar.',
      secreto: password,
      copiar: `Email: ${email}\nContrasena: ${password}`
    });
    // Refrescar lista
    window.__nxLoadBizCredentials && window.__nxLoadBizCredentials(bizId);
  };

  renderShell(container);
  if (currentTab === 'info') loadMarca(); // FND-T1: la marca se pide al pintar la pestana Info
  loadSubscriptionStatusBanner(); // SUBS-AUTO-RESTORE-001 T6: banner global

  if (!delegationBound) {
    bindDelegation(container);
    delegationBound = true;
  }
}

// SUBS-AUTO-RESTORE-001 T6: detecta si el negocio tiene sub vencida y muestra banner global.
// - daysOver <= 5 (gracia): warning amarillo "renovar antes de bloqueo"
// - daysOver > 5 (fuera gracia): error rojo "cliente Desktop bloqueado"
async function loadSubscriptionStatusBanner() {
  const $banner = document.getElementById('nx-biz-sub-banner');
  if (!$banner) return;

  const result = await SubscriptionApiClient.list(businessId);
  if (!result.ok) return;

  const subs = result.data?.data || result.data || [];
  if (subs.length === 0) return;

  // Tomar la mas reciente por endDate (igual que la API LATERAL JOIN)
  const sub = subs.slice().sort((a, b) => new Date(b.endDate || 0) - new Date(a.endDate || 0))[0];
  if (!sub || !sub.endDate) return;

  const endDate = new Date(sub.endDate);
  const now = new Date();
  const msMs = now - endDate;
  const daysOver = Math.ceil(msMs / 86400000);

  // Solo banner si esta vencida (daysOver >= 0). Activa o futura no necesita banner.
  if (daysOver < 0) return;

  const fmtFecha = formatearFecha(endDate, { day: '2-digit', month: 'short', year: 'numeric' });

  if (daysOver <= 5) {
    // Gracia activa
    const restantes = 5 - daysOver;
    $banner.hidden = false;
    $banner.innerHTML = `
      <div style="background:rgba(255,196,0,0.1);border:1px solid rgba(255,196,0,0.3);border-radius:6px;padding:12px 16px;color:#e6a700;font-size:0.9rem">
        &#9888; <strong>Suscripcion vencida el ${fmtFecha}.</strong>
        Periodo de gracia activo: el cliente Desktop sigue operando ${restantes} dia${restantes === 1 ? '' : 's'} mas.
        <a href="#" onclick="document.querySelector('[data-tab=suscripcion]')?.click(); return false;" style="color:#79c0ff;margin-left:8px">Ver suscripciones &rarr;</a>
      </div>`;
  } else {
    // Fuera de gracia
    $banner.hidden = false;
    $banner.innerHTML = `
      <div style="background:rgba(248,81,73,0.1);border:1px solid rgba(248,81,73,0.4);border-radius:6px;padding:12px 16px;color:#f85149;font-size:0.9rem">
        &#9888; <strong>Suscripcion vencida el ${fmtFecha} (hace ${daysOver} dias).</strong>
        Cliente Desktop <strong>BLOQUEADO</strong> (fuera del periodo de gracia de 5 dias).
        Renovar la suscripcion para reanudar operacion.
        <a href="#" onclick="document.querySelector('[data-tab=suscripcion]')?.click(); return false;" style="color:#79c0ff;margin-left:8px">Renovar suscripcion &rarr;</a>
      </div>`;
  }
}

function renderShell(container) {
  const b = currentBiz;
  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml({ volverA: "#/negocios", texto: "Negocios" })}
          <h1 id="nx-biz-title">${esc(b.displayName || '(sin nombre)')}</h1>
        </div>
        <div class="task-actions">
          ${bizStatusBadge(b.status)}
          <button class="btn-ghost btn-sm" id="nx-biz-edit">Editar</button>
          ${estaArchivado(b)
            ? '<button class="btn-ghost btn-sm" id="nx-biz-desarchivar" title="Vuelve a activo sin tocar su historia">Desarchivar</button>'
            : '<button class="btn-ghost btn-sm" id="nx-biz-archivar" title="Ya no trabaja con Nexo: sale de las listas y apaga sus equipos; su historia se conserva">Archivar</button>'}
          <button class="btn-icon btn-delete" id="nx-biz-delete" title="Eliminar negocio (solo sin ventas, equipos ni pagos)">&times;</button>
        </div>
      </div>

      <!-- SUBS-AUTO-RESTORE-001 T6: Banner global suscripcion vencida (fuera de gracia) o gracia activa -->
      <div id="nx-biz-sub-banner" style="margin:0 0 1rem 0" hidden></div>

      <nav class="detail-tabs">
        ${TABS.map(t => `
          <button class="detail-tab${t === currentTab ? ' active' : ''}" data-tab="${t}">
            ${TAB_NAMES[t]}
          </button>`).join('')}
      </nav>

      <div id="nx-biz-panel" class="detail-panel">
        ${buildPanel(currentTab, b, false)}
      </div>

      <p id="nx-biz-status" class="link-status" hidden></p>
    </section>`;
}

// PANEL-OPS-V2-001 T3 — Helpers form Configuracion operativa
function __nxBuildOpsConfigForm(bizId, cfg) {
  return `
    <form id="nx-ops-config-form" data-biz-id="${esc(bizId)}" style="display:flex;flex-direction:column;gap:10px">
      <label style="display:flex;align-items:center;gap:8px;font-size:0.875rem;color:#c9d1d9">
        <input type="checkbox" id="nx-ops-strict" ${cfg.asistencia_strict_mode ? 'checked' : ''}>
        Modo estricto asistencia
        <small style="color:#8b949e;margin-left:4px">(bloquea secuencias invalidas; sino solo advierte)</small>
      </label>
      <div style="display:flex;gap:10px;flex-wrap:wrap">
        <div style="display:flex;flex-direction:column;gap:4px;min-width:180px;flex:1">
          <label for="nx-ops-umbral" style="font-size:0.875rem;color:#8b949e">Umbral de tardanza (minutos)</label>
          <input type="number" id="nx-ops-umbral" class="form-input" min="0" max="120" step="1" value="${esc(String(cfg.umbral_tardanza_minutos ?? 5))}">
        </div>
        <div style="display:flex;flex-direction:column;gap:4px;min-width:180px;flex:1">
          <label for="nx-ops-gracia" style="font-size:0.875rem;color:#8b949e">Periodo de gracia (dias)</label>
          <input type="number" id="nx-ops-gracia" class="form-input" min="0" max="60" step="1" value="${esc(String(cfg.periodo_gracia_dias ?? 7))}">
        </div>
      </div>
      <div style="display:flex;align-items:center;gap:10px;margin-top:4px">
        <button type="button" class="btn-primary btn-sm"
                onclick="window.__nxSaveOpsConfig && window.__nxSaveOpsConfig('${esc(bizId)}')">Guardar</button>
        <span id="nx-ops-save-status" style="font-size:0.875rem"></span>
      </div>
    </form>`;
}

function __nxBindOpsConfigForm(_bizId) {
  // Reservado: hooks si agregamos validaciones live (por ahora solo boton guardar).
}

function buildPanel(tab, b, editing) {
  switch (tab) {
    case 'info':         return buildInfoPanel(b, editing);
    case 'contacto':     return buildContactoPanel(b, editing);
    case 'propietario':  return buildPropietarioPanel(b, editing);
    case 'equipos':      return '<p class="empty-state">Cargando equipos...</p>';
    case 'instalaciones': return '<p class="empty-state">Cargando instalaciones...</p>';
    case 'suscripcion':  return buildSuscripcionPanel(b);
    case 'apariencia':   return buildAparienciaPanel(b, editing);
    default:             return '';
  }
}

/** Normaliza URL de Dropbox a descarga directa (misma lógica que ImageDownloadService.vb) */
function normDropbox(url) {
  if (!url || !url.includes('dropbox.com')) return url;
  if (url.includes('raw=1')) return url;
  if (url.includes('dl=0')) return url.replace('dl=0', 'raw=1');
  if (url.includes('dl=1')) return url.replace('dl=1', 'raw=1');
  return url.includes('?') ? url + '&raw=1' : url + '?raw=1';
}

function acfg(b) {
  return b.accountConfig || {};
}

function buildAparienciaPanel(b, editing) {
  const cfg = acfg(b);
  const logoUrl  = cfg.logo_url  || '';
  const fondoUrl = cfg.fondo_url || '';
  const colP = cfg.color_primario   || '';
  const colS = cfg.color_secundario || '';
  const colT = cfg.color_texto      || '';
  const personajeUrl = cfg.personaje_url || '';
  const personajeCat = cfg.personaje_categoria || 'marca';
  const fontName     = cfg.font_name || '';

  const logoRaw  = normDropbox(logoUrl);
  const fondoRaw = normDropbox(fondoUrl);

  const previewLogo = logoRaw
    ? `<img src="${esc(logoRaw)}" alt="Logo" style="max-width:120px;max-height:80px;object-fit:contain;border-radius:4px;background:#1e1e2e;padding:4px">`
    : `<div style="width:80px;height:80px;background:#1e1e2e;border:1px solid #30363d;border-radius:4px;display:flex;align-items:center;justify-content:center;color:#6e7681;font-size:0.875rem">Sin logo</div>`;

  const previewFondo = fondoRaw
    ? `<img src="${esc(fondoRaw)}" alt="Fondo" style="max-width:200px;max-height:100px;object-fit:cover;border-radius:4px">`
    : `<div style="width:200px;height:80px;background:#1e1e2e;border:1px solid #30363d;border-radius:4px;display:flex;align-items:center;justify-content:center;color:#6e7681;font-size:0.875rem">Sin fondo</div>`;

  if (editing) {
    return `
      <form id="nx-apariencia-form">
        <div style="display:flex;flex-direction:column;gap:1.25rem;max-width:640px">

          <div class="detail-field">
            <label class="detail-label" for="ap-logoUrl">URL del logo</label>
            <input type="url" id="ap-logoUrl" class="form-input" value="${esc(logoUrl)}"
              placeholder="https://www.dropbox.com/scl/fi/..."
              oninput="document.getElementById('ap-logo-preview').src=this.value.replace('dl=0','raw=1').replace('dl=1','raw=1')">
            <span style="font-size:0.875rem;color:#6e7681;margin-top:2px">Dropbox: pega el link compartido (dl=0 se normaliza automáticamente)</span>
          </div>

          <div class="detail-field" data-ap-logo-archivo>
            <label class="detail-label" for="ap-logo-archivo">…o sube el archivo del logo</label>
            <input type="file" id="ap-logo-archivo" class="form-input" accept="image/png,image/jpeg">
            <span style="font-size:0.875rem;color:#6e7681;margin-top:2px">PNG con fondo transparente se ve mejor sobre el fondo del Desktop. Maximo 5 MB. Al subirlo, la direccion queda en el campo de arriba.</span>
            <span id="ap-logo-archivo-estado" style="font-size:0.875rem;margin-top:4px"></span>
          </div>

          <div style="display:flex;align-items:center;gap:1rem">
            <img id="ap-logo-preview" src="${esc(logoRaw)}" alt=""
              style="max-width:120px;max-height:80px;object-fit:contain;background:#1e1e2e;padding:4px;border-radius:4px;border:1px solid #30363d"
              onerror="this.style.display='none'" onload="this.style.display=''">
            <span style="color:#6e7681;font-size:0.875rem">Vista previa del logo</span>
          </div>

          ${fondoUrl && isDataImageUrl(fondoUrl) ? `
          <div class="detail-field">
            <label class="detail-label">Imagen de fondo</label>
            <div id="ap-fondo-ia-preview" style="display:flex;align-items:center;gap:0.75rem;padding:0.6rem 0.75rem;background:#0d1117;border:1px solid #30363d;border-radius:6px;margin-bottom:4px">
              <img src="${esc(fondoRaw)}" alt="" style="height:48px;width:80px;object-fit:cover;border-radius:3px;flex-shrink:0">
              <div style="flex:1;min-width:0">
                <div style="font-size:0.875rem;color:#c9d1d9;font-weight:500">✨ Fondo generado con IA</div>
                <div style="font-size:0.875rem;color:#6e7681;margin-top:2px">Para cambiar, genera uno nuevo en la sección de abajo</div>
              </div>
              <button type="button" onclick="document.getElementById('ap-fondoUrl').value='';document.getElementById('ap-fondo-ia-preview').remove()" style="background:none;border:none;cursor:pointer;color:#f85149;font-size:0.875rem;padding:2px 8px;flex-shrink:0">🗑 Quitar</button>
            </div>
            <input type="hidden" id="ap-fondoUrl" value="${esc(fondoUrl)}">
            <span style="font-size:0.875rem;color:#6e7681">Imagen de fondo para la pantalla principal del Desktop</span>
          </div>` : `
          <div class="detail-field">
            <label class="detail-label" for="ap-fondoUrl">URL del fondo</label>
            <input type="url" id="ap-fondoUrl" class="form-input" value="${esc(fondoUrl)}"
              placeholder="https://www.dropbox.com/scl/fi/..."
              oninput="document.getElementById('ap-fondo-preview').src=this.value.replace('dl=0','raw=1').replace('dl=1','raw=1')">
            <span style="font-size:0.875rem;color:#6e7681;margin-top:2px">Imagen de fondo para la pantalla principal del Desktop</span>
          </div>`}

          <div class="detail-field" data-ap-fondo-archivo>
            <label class="detail-label" for="ap-fondo-archivo">…o sube la imagen del fondo</label>
            <input type="file" id="ap-fondo-archivo" class="form-input" accept="image/png,image/jpeg">
            <span style="font-size:0.875rem;color:#6e7681;margin-top:2px">PNG o JPG, maximo 5 MB. Lo recomendado es horizontal 16:9 (por ejemplo 1920 x 1080). Reemplaza el fondo actual; el link sigue sirviendo.</span>
            <span id="ap-fondo-archivo-estado" style="font-size:0.875rem;margin-top:4px"></span>
          </div>

          <div style="display:flex;align-items:center;gap:1rem">
            <img id="ap-fondo-preview" src="${esc(fondoRaw)}" alt=""
              style="max-width:220px;max-height:100px;object-fit:cover;border-radius:4px;border:1px solid #30363d"
              onerror="this.style.display='none'" onload="this.style.display=''">
            <span style="color:#6e7681;font-size:0.875rem">Vista previa del fondo</span>
          </div>

          <div class="detail-field">
            <label class="detail-label" for="ap-personajeUrl">Personaje / Mascota</label>
            <input type="url" id="ap-personajeUrl" class="form-input" value="${esc(personajeUrl)}"
              placeholder="Sube una imagen de tu personaje, mascota o representante del negocio (opcional)"
              oninput="document.getElementById('ap-personaje-preview').src=this.value.replace('dl=0','raw=1').replace('dl=1','raw=1')">
            <span style="font-size:0.875rem;color:#6e7681;margin-top:2px">Imagen opcional del personaje o mascota del negocio (Dropbox link)</span>
          </div>

          <div style="display:flex;align-items:center;gap:1rem">
            <img id="ap-personaje-preview" src="${esc(normDropbox(personajeUrl))}" alt=""
              style="max-width:120px;max-height:80px;object-fit:contain;background:#1e1e2e;padding:4px;border-radius:4px;border:1px solid #30363d"
              onerror="this.style.display='none'" onload="this.style.display=''">
            <span style="color:#6e7681;font-size:0.875rem">Vista previa del personaje</span>
          </div>

          <div class="detail-field" style="max-width:280px">
            <label class="detail-label" for="ap-personajeCat">Usar personaje en categoría</label>
            <select id="ap-personajeCat" class="form-input">
              <option value="oferta"${personajeCat === 'oferta' ? ' selected' : ''}>Oferta</option>
              <option value="servicio"${personajeCat === 'servicio' ? ' selected' : ''}>Servicio</option>
              <option value="marca"${personajeCat === 'marca' ? ' selected' : ''}>Marca</option>
              <option value="campana"${personajeCat === 'campana' ? ' selected' : ''}>Campaña</option>
              <option value="interaccion"${personajeCat === 'interaccion' ? ' selected' : ''}>Interacción</option>
              <option value="informativa"${personajeCat === 'informativa' ? ' selected' : ''}>Informativa</option>
            </select>
            <span style="font-size:0.875rem;color:#6e7681;margin-top:2px">El personaje solo aparecerá cuando el tipo de publicidad coincida con esta categoría</span>
          </div>

          <div class="detail-field">
            <label class="detail-label" for="ap-fontName">Tipo de Letra</label>
            <input type="text" id="ap-fontName" class="form-input" value="${esc(fontName)}"
              placeholder="Ej: Montserrat, Poppins, Roboto Slab...">
            <span style="font-size:0.875rem;color:#6e7681;margin-top:2px">Nombre de la tipografía que usa el negocio (opcional)</span>
          </div>

          <details style="border:1px solid #30363d;border-radius:6px;padding:0.75rem">
            <summary style="cursor:pointer;color:#8b949e;font-size:0.875rem;user-select:none">Colores corporativos (opcional)</summary>
            <div style="display:flex;gap:1rem;margin-top:0.75rem;flex-wrap:wrap">
              <div class="detail-field" style="flex:1;min-width:140px">
                <label class="detail-label" for="ap-colP">Color primario</label>
                <div style="display:flex;gap:6px;align-items:center">
                  <input type="color" id="ap-colP-picker" value="${colP || '#00c3d2'}" style="width:32px;height:32px;border:none;background:none;cursor:pointer"
                    oninput="document.getElementById('ap-colP').value=this.value">
                  <input type="text" id="ap-colP" class="form-input" value="${esc(colP)}" placeholder="#00c3d2" style="flex:1">
                </div>
              </div>
              <div class="detail-field" style="flex:1;min-width:140px">
                <label class="detail-label" for="ap-colS">Color secundario</label>
                <div style="display:flex;gap:6px;align-items:center">
                  <input type="color" id="ap-colS-picker" value="${colS || '#1a1a2e'}" style="width:32px;height:32px;border:none;background:none;cursor:pointer"
                    oninput="document.getElementById('ap-colS').value=this.value">
                  <input type="text" id="ap-colS" class="form-input" value="${esc(colS)}" placeholder="#1a1a2e" style="flex:1">
                </div>
              </div>
              <div class="detail-field" style="flex:1;min-width:140px">
                <label class="detail-label" for="ap-colT">Color de texto</label>
                <div style="display:flex;gap:6px;align-items:center">
                  <input type="color" id="ap-colT-picker" value="${colT || '#ffffff'}" style="width:32px;height:32px;border:none;background:none;cursor:pointer"
                    oninput="document.getElementById('ap-colT').value=this.value">
                  <input type="text" id="ap-colT" class="form-input" value="${esc(colT)}" placeholder="#ffffff" style="flex:1">
                </div>
              </div>
            </div>
          </details>

          <div class="detail-form-actions">
            <button type="submit" class="btn-primary btn-sm" id="nx-apariencia-save">Guardar apariencia</button>
            <button type="button" class="btn-ghost btn-sm" id="nx-apariencia-cancel">Cancelar</button>
            <span id="nx-apariencia-err" class="auth-error" hidden></span>
          </div>
        </div>
      </form>`;
  }

  const bizType  = esc(b.businessType || '');
  const bizName  = esc(b.displayName  || '');

  return `
    <div style="display:flex;flex-direction:column;gap:1.5rem;max-width:620px">

      <div style="display:flex;gap:1.5rem;align-items:flex-start;flex-wrap:wrap">
        <div>
          <p class="detail-label" style="margin-bottom:6px">Logo del negocio</p>
          ${previewLogo}
          ${logoUrl ? `<p style="font-size:0.875rem;color:#6e7681;margin-top:4px;word-break:break-word;max-width:280px">${esc(shortenUrlCaption(logoUrl))}</p>` : ''}
        </div>
        <div>
          <p class="detail-label" style="margin-bottom:6px">Imagen de fondo</p>
          ${previewFondo}
          ${fondoUrl ? `<p style="font-size:0.875rem;color:#6e7681;margin-top:4px;word-break:break-word;max-width:280px">${isDataImageUrl(fondoUrl) ? '✨ Fondo generado con IA (datos guardados en el perfil; no es un enlace a Dropbox).' : esc(shortenUrlCaption(fondoUrl))}</p>` : ''}

          <button
            id="nx-generar-fondo-btn"
            class="btn-ghost btn-sm"
            style="margin-top:8px;display:flex;align-items:center;gap:5px;font-size:0.875rem"
            ${!logoUrl ? 'title="Primero configura el logo del negocio"' : ''}>
            ✨ Generar fondo con IA
          </button>
        </div>
      </div>

      ${personajeUrl ? `
      <div>
        <p class="detail-label" style="margin-bottom:6px">Personaje / Mascota</p>
        <div style="display:flex;align-items:center;gap:1rem">
          <img src="${esc(normDropbox(personajeUrl))}" alt="Personaje" style="max-width:120px;max-height:80px;object-fit:contain;border-radius:4px;background:#1e1e2e;padding:4px">
          <span style="font-size:0.875rem;color:#8b949e">Categoría: <strong>${esc(personajeCat)}</strong></span>
        </div>
      </div>` : ''}

      ${fontName ? `
      <div>
        <p class="detail-label" style="margin-bottom:6px">Tipo de Letra</p>
        <p style="font-size:0.875rem;color:#c9d1d9">${esc(fontName)}</p>
      </div>` : ''}

      ${(colP || colS || colT) ? `
      <div>
        <p class="detail-label" style="margin-bottom:8px">Colores corporativos</p>
        <div style="display:flex;gap:0.75rem;flex-wrap:wrap">
          ${colP ? `<div style="display:flex;align-items:center;gap:6px"><span style="width:18px;height:18px;border-radius:3px;background:${esc(colP)};border:1px solid #30363d;display:inline-block"></span><span style="font-size:0.875rem;color:#8b949e">Primario: ${esc(colP)}</span></div>` : ''}
          ${colS ? `<div style="display:flex;align-items:center;gap:6px"><span style="width:18px;height:18px;border-radius:3px;background:${esc(colS)};border:1px solid #30363d;display:inline-block"></span><span style="font-size:0.875rem;color:#8b949e">Secundario: ${esc(colS)}</span></div>` : ''}
          ${colT ? `<div style="display:flex;align-items:center;gap:6px"><span style="width:18px;height:18px;border-radius:3px;background:${esc(colT)};border:1px solid #30363d;display:inline-block"></span><span style="font-size:0.875rem;color:#8b949e">Texto: ${esc(colT)}</span></div>` : ''}
        </div>
      </div>` : ''}

      <div id="nx-fondo-cache-hint" hidden style="margin-top:10px;padding:0.75rem 1rem;background:#21262d;border:1px solid #388bfd44;border-radius:8px;font-size:0.875rem;color:#c9d1d9">
        <p id="nx-fondo-cache-hint-text" style="margin:0 0 0.6rem 0;line-height:1.45"></p>
        <div style="display:flex;flex-wrap:wrap;gap:8px">
          <button type="button" id="nx-fondo-restore-cache" class="btn-primary btn-sm" style="font-size:0.875rem">Ver últimas variantes guardadas</button>
          <button type="button" id="nx-fondo-discard-cache" class="btn-ghost btn-sm" style="font-size:0.875rem">Descartar copia en este dispositivo</button>
        </div>
      </div>

      <!-- Panel de resultado de generación de fondo (contenido inyectado por handleGenerarFondo / caché local) -->
      <div id="nx-fondo-result" hidden style="border:1px solid #30363d;border-radius:8px;padding:1rem;background:#0d1117">
        <div id="nx-fondo-result-inner"></div>
      </div>

      ${!logoUrl && !fondoUrl ? '<p class="empty-state" style="margin:0">Sin configuración de apariencia. Haz clic en Editar para agregar logo y fondo.</p>' : ''}
    </div>`;
}

/**
 * EQUIPOS-PANEL-T1 parte 2: lo que la ronda 2026-10 confirmo de este negocio (businesses.metadata.ronda_2026_10), solo
 * lectura, mientras llegan las columnas de verdad (D-85). Las reglas viven en application/negocios/rondaDelNegocio.js.
 */
function seccionRonda(b) {
  const r = filasDeLaRonda(b);
  if (!r.hay) {
    return `<p data-ronda="sin" style="margin:0 0 12px;font-size:0.875rem;color:#8b949e">Ronda 2026-10: este negocio aun no tiene datos de la ronda.</p>`;
  }
  if (r.ilegible) {
    return `<p data-ronda="ilegible" class="auth-error" style="margin:0 0 12px">Ronda 2026-10: el dato guardado no se pudo leer (no es JSON).</p>`;
  }
  const pie = r.completa
    ? `Revisada el ${esc(r.fecha)} por ${esc(r.por)}`
    : 'Solo lo medido en el codigo; falta revisarla con Josse';
  return `
    <details data-ronda="${r.completa ? 'completa' : 'parcial'}" open style="margin:0 0 16px;padding:12px 14px;background:#0d1117;border:1px solid #30363d;border-radius:8px">
      <summary style="cursor:pointer;font-weight:600;color:#c9d1d9">Ronda 2026-10 <small style="font-weight:normal;color:#8b949e">&middot; ${pie} &middot; solo lectura</small></summary>
      <dl style="display:grid;grid-template-columns:minmax(150px,220px) 1fr;gap:6px 14px;margin:10px 0 0;font-size:0.875rem">
        ${r.filas.map((f) => `
          <dt data-ronda-clave="${esc(f.clave)}" style="color:#8b949e">${esc(f.etiqueta)}</dt>
          <dd style="margin:0;${f.pendiente ? 'color:#d29922;font-weight:600' : 'color:#c9d1d9'};overflow-wrap:anywhere">${esc(f.valor)}</dd>`).join('')}
      </dl>
    </details>`;
}

/**
 * FND-T1: la descripcion de marca del negocio (que vende o hace, productos tipicos, valores y color), la que usan los
 * prompts de los fondos (FND-T2). Se lee y se guarda en GET/PUT /api/businesses/{id}/marca y, tras guardar, se RELEE:
 * lo que se pinta es lo que quedo en la API, no lo que se escribio. Reglas en application/negocios/marcaDelNegocio.js.
 */
let _marcaVuelta = 0;
async function loadMarca() {
  const $m = document.querySelector('[data-marca-negocio]');
  if (!$m || !currentBiz) return;
  const id = currentBiz.id || currentBiz.businessId;
  const vuelta = ++_marcaVuelta;
  let res;
  try { res = await BusinessApiClient.getMarca(id); } catch { res = { ok: false }; }
  if (vuelta !== _marcaVuelta || !document.contains($m)) return;
  if (res.ok) pintarMarca($m, leerMarca(res.data?.data ?? res.data), null);
  else pintarMarca($m, leerMarca(null), motivoDeLaMarcaDelNegocio(res));
}

function pintarMarca($m, marca, aviso) {
  const falta = marca.falta || [];
  const estado = aviso ? aviso.motivo : (marca.completa ? 'completa' : 'incompleta');
  const resumen = aviso ? 'sin leer'
    : marca.completa ? 'completa: los fondos ya pueden usarla'
    : 'falta: ' + falta.map((f) => ETIQUETAS[f] || f).join(', ');
  const campo = (id, etiqueta, valor, ph, extra = '') => `
    <div class="detail-field"${extra}>
      <label class="detail-label" for="${id}">${etiqueta}${falta.includes(id.replace('marca-', '')) && !aviso ? ' <span style="color:#d29922">(falta)</span>' : ''}</label>
      <input id="${id}" class="form-input" value="${esc(valor)}" placeholder="${esc(ph)}" autocomplete="off">
    </div>`;
  $m.innerHTML = `
    <details data-marca-estado="${esc(estado)}" open style="padding:12px 14px;background:#0d1117;border:1px solid #30363d;border-radius:8px">
      <summary style="cursor:pointer;font-weight:600;color:#c9d1d9">Marca del negocio <small style="font-weight:normal;color:${marca.completa && !aviso ? '#3fb950' : '#d29922'}">&middot; ${esc(resumen)}</small></summary>
      ${aviso ? `<p data-marca-motivo="${esc(aviso.motivo)}" class="auth-error" style="margin:10px 0 0">${esc(aviso.frase)}</p>` : ''}
      <form id="nx-marca-form" class="detail-grid detail-grid-edit" style="margin-top:10px">
        ${campo('marca-descripcion', ETIQUETAS.descripcion, marca.descripcion, 'Ej.: restaurante de comida mexicana', ' style="grid-column:1/-1"')}
        ${campo('marca-productosTipicos', ETIQUETAS.productosTipicos, marca.productosTipicos.join(', '), 'Separados por coma: tacos, burritos, quesadillas', ' style="grid-column:1/-1"')}
        ${campo('marca-valores', ETIQUETAS.valores, marca.valores.join(', '), 'Separados por coma: sabor casero, alegria')}
        <div class="detail-field">
          <label class="detail-label" for="marca-colorHex">${ETIQUETAS.colorHex}${falta.includes('colorHex') && !aviso ? ' <span style="color:#d29922">(falta)</span>' : ''}</label>
          <div style="display:flex;gap:8px;align-items:center">
            <input id="marca-colorPick" type="color" value="${esc(marca.colorHex || '#238636')}" style="width:40px;height:36px;padding:0;border:0;background:none" aria-label="Elegir color">
            <input id="marca-colorHex" class="form-input" value="${esc(marca.colorHex || '')}" placeholder="#E31837" style="flex:none;width:12ch;max-width:12ch;min-width:0" autocomplete="off">
            <input id="marca-colorNombre" class="form-input" value="${esc(marca.colorNombre)}" placeholder="Nombre: rojo tomate" style="min-width:0;flex:1" autocomplete="off" aria-label="Nombre del color">
          </div>
          <span data-marca-color-una-fuente style="font-size:0.875rem;color:#8b949e;margin-top:2px">Es el color principal de la Apariencia: cambiarlo aqui cambia tambien el Desktop y los fondos.</span>
        </div>
        <p style="grid-column:1/-1;margin:0;font-size:0.875rem;color:#8b949e">Ejemplos: ${EJEMPLOS.map(esc).join(' &middot; ')}</p>
        <div class="detail-form-actions">
          <button type="submit" class="btn-primary btn-sm" id="nx-marca-save">Guardar marca</button>
          <span id="nx-marca-msg" data-marca-msg style="font-size:0.875rem" hidden></span>
        </div>
      </form>
    </details>`;
  const $pick = $m.querySelector('#marca-colorPick'), $hex = $m.querySelector('#marca-colorHex');
  $pick.addEventListener('input', () => { $hex.value = $pick.value.toUpperCase(); });
  $m.querySelector('#nx-marca-form').addEventListener('submit', (ev) => { ev.preventDefault(); guardarMarcaDeLaFicha($m); });
}

async function guardarMarcaDeLaFicha($m) {
  const v = (id) => $m.querySelector('#' + id)?.value ?? '';
  const cuerpo = cuerpoDeMarca({
    descripcion: v('marca-descripcion'), productosTipicos: v('marca-productosTipicos'), valores: v('marca-valores'),
    colorHex: v('marca-colorHex'), colorNombre: v('marca-colorNombre')
  });
  const $btn = $m.querySelector('#nx-marca-save'), $msg = $m.querySelector('#nx-marca-msg');
  $btn.disabled = true; $btn.textContent = 'Guardando...';
  let res;
  try { res = await BusinessApiClient.guardarMarca(currentBiz.id || currentBiz.businessId, cuerpo); } catch { res = { ok: false }; }
  $btn.disabled = false; $btn.textContent = 'Guardar marca';
  if (!res.ok) {
    $msg.hidden = false; $msg.style.color = '#f85149'; $msg.textContent = motivoDeLaMarcaDelNegocio(res).frase;
    return;
  }
  // Se relee: se pinta lo que quedo en la API. Si falta algo, se dice que se guardo a medias.
  await loadMarca();
  const falta = loQueFalta(cuerpo);
  // MARCA-COLORES-UNA-FUENTE-T1 (api 552): el color de la marca es el principal de la Apariencia; la pestana Apariencia
  // ya cargada lo muestra al dia sin recargar la pagina.
  if (cuerpo.colorHex && currentBiz?.accountConfig) currentBiz.accountConfig.color_primario = String(cuerpo.colorHex).toLowerCase();
  showStatus(falta.length ? 'Marca guardada; falta: ' + falta.map((f) => ETIQUETAS[f] || f).join(', ') : 'Marca guardada completa');
}

function buildInfoPanel(b, editing) {
  if (editing) {
    return `
      <form id="nx-biz-form" class="detail-grid detail-grid-edit">
        ${fieldInput('biz-displayName', 'Nombre comercial', b.displayName)}
        ${fieldInput('biz-legalName',   'Razón social',     b.legalName)}
        ${fieldInput('biz-taxId',       'NIT / RUT',        b.taxId)}
        ${fieldSelect('biz-businessType', 'Tipo de negocio', b.businessType, opcionesConValorActual(_catalogo.tipos, b.businessType))}
        ${fieldSelect('biz-plan',         'Plan',            b.plan,         opcionesConValorActual(_catalogo.planes, b.plan))}
        ${_catalogo.estado === 'ok' ? '' : `<p data-catalogo-aviso class="auth-error" style="grid-column:1/-1">${esc(MENSAJE_SIN_CATALOGO)}</p>`}
        <div class="detail-field">
          <label class="detail-label" for="biz-startDate">Fecha de inicio</label>
          <input type="date" id="biz-startDate" class="form-input" value="${isoToDate(b.createdAt)}">
        </div>
        <div class="detail-field detail-field-ro">
          <span class="detail-label">Slug</span>
          <code class="detail-value-mono">${esc(b.slug)}</code>
        </div>
        <div class="detail-field detail-field-ro" style="grid-column:1/-1">
          <span class="detail-label">Business ID <small style="color:#8b949e">(codigo instalacion Desktop)</small></span>
          <div style="display:flex;align-items:center;gap:8px">
            <code class="detail-value-mono" style="font-size:14px;user-select:all">${esc(b.id || b.businessId)}</code>
            <button type="button" class="btn-ghost btn-sm" style="padding:2px 8px;font-size:14px;cursor:pointer"
                    onclick="navigator.clipboard.writeText('${esc(b.id || b.businessId)}').then(()=>{this.textContent='Copiado!';setTimeout(()=>this.textContent='Copiar',2000)})">Copiar</button>
          </div>
        </div>
        <div class="detail-form-actions">
          <button type="submit" class="btn-primary btn-sm" id="nx-biz-save">Guardar</button>
          <button type="button" class="btn-ghost btn-sm" id="nx-biz-cancel">Cancelar</button>
          <span id="nx-biz-save-err" class="auth-error" hidden></span>
        </div>
      </form>`;
  }
  return `
    ${seccionRonda(b)}
    <div data-marca-negocio style="margin:0 0 16px"><p class="empty-state" style="margin:0">Cargando la marca del negocio...</p></div>
    <div class="detail-grid">
      ${fieldRO('Nombre comercial', esc(b.displayName))}
      ${fieldRO('Razón social',     esc(b.legalName))}
      ${fieldRO('NIT / RUT',        esc(b.taxId))}
      ${fieldRO('Tipo de negocio',  esc(b.businessType))}
      ${fieldRO('Plan',             esc(b.plan))}
      ${fieldRO('Fecha de inicio',  fmt(b.createdAt))}
      <div class="detail-field">
        <span class="detail-label">Slug</span>
        <code class="detail-value-mono">${esc(b.slug)}</code>
      </div>
      <div class="detail-field">
        <span class="detail-label">Business ID <small style="color:#8b949e">(codigo de instalacion Desktop)</small></span>
        <div style="display:flex;align-items:center;gap:8px">
          <code class="detail-value-mono" style="font-size:14px;user-select:all">${esc(b.businessId || b.id)}</code>
          <button class="btn-ghost btn-sm" style="padding:2px 8px;font-size:14px;cursor:pointer"
                  onclick="navigator.clipboard.writeText('${esc(b.businessId || b.id)}').then(()=>{this.textContent='Copiado!';setTimeout(()=>this.textContent='Copiar',2000)})">Copiar</button>
        </div>
      </div>
      <div class="detail-field" style="grid-column:1/-1;margin-top:8px">
        ${puedeInvitar(AppConfig.role) ? `<button class="btn-ghost btn-sm" id="nx-biz-send-onboarding"
                style="padding:6px 16px;border:1px solid #30363d;cursor:pointer"
                onclick="window.__nxSendOnboarding && window.__nxSendOnboarding('${esc(idDelNegocio(b))}','${esc(b.displayName)}')">
          📩 Enviar invitación de registro al cliente
        </button>` : `<p data-invitacion-nexo style="margin:0;font-size:0.875rem;color:#8b949e">${esc(INVITACION_LA_MANDA_NEXO)}</p>`}
        <small style="color:#8b949e;margin-left:8px">Genera un link para que el cliente llene sus datos y acepte términos</small>
        <div id="nx-invitacion-link" data-invitacion hidden style="margin-top:10px;padding:10px 12px;background:#0d1117;border:1px solid #30363d;border-radius:8px"></div>
      </div>

      <div class="detail-field" style="grid-column:1/-1;margin-top:16px;padding:12px;background:#0d1117;border:1px solid #30363d;border-radius:8px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
          <span class="detail-label" style="font-weight:600;color:#c9d1d9">🔐 Credenciales panel del negocio</span>
          <a href="${esc(rutaPersonasDelNegocio(b.displayName))}" data-personas-del-negocio class="btn-ghost btn-sm" style="padding:4px 10px;font-size:14px;margin-left:auto;margin-right:6px"
             title="Personas de este negocio: quien entra al panel, quien tiene PIN, cargo y su ficha">👥 Ver sus personas</a>
          <button class="btn-ghost btn-sm" id="nx-biz-load-credentials" data-biz-id="${esc(b.id || b.businessId)}"
                  style="padding:4px 10px;font-size:14px;cursor:pointer"
                  onclick="window.__nxLoadBizCredentials && window.__nxLoadBizCredentials('${esc(b.id || b.businessId)}')">Cargar usuarios</button>
        </div>
        <div id="nx-biz-credentials-body" style="font-size:0.875rem;color:#8b949e">
          Click en "Cargar usuarios" para ver el email del negocio y regenerar contrasena.
        </div>
      </div>

      <!-- PANEL-OPS-V2-001 T3 — Configuracion operativa -->
      <div class="detail-field" id="nx-ops-config-section" data-biz-id="${esc(b.id || b.businessId)}" style="grid-column:1/-1;margin-top:16px;padding:12px;background:#0d1117;border:1px solid #30363d;border-radius:8px">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
          <span class="detail-label" style="font-weight:600;color:#c9d1d9">⚙️ Configuracion operativa</span>
          <button class="btn-ghost btn-sm" style="padding:4px 10px;font-size:14px;cursor:pointer"
                  onclick="window.__nxLoadOpsConfig && window.__nxLoadOpsConfig('${esc(b.id || b.businessId)}')">Cargar</button>
        </div>
        <div id="nx-ops-config-body" style="font-size:0.875rem;color:#8b949e">
          Click en "Cargar" para ver la configuracion de asistencia del negocio.
        </div>
      </div>
    </div>`;
}

function buildContactoPanel(b, editing) {
  const dept = b.state || '';
  // Nota: phoneHasWhatsapp default true cuando el campo no existe en BD (mayoria de negocios si tienen WA)
  const phoneWA = b.phoneHasWhatsapp !== false;
  if (editing) {
    return `
      <form id="nx-biz-form" class="detail-grid detail-grid-edit">
        ${fieldInput('biz-phone',        'Teléfono del negocio', b.phone, 'tel', '+57 300 000 0000')}
        <div class="detail-field">
          <label style="display:flex;align-items:center;gap:8px;cursor:pointer;padding-top:6px">
            <input type="checkbox" id="biz-phoneHasWhatsapp" ${phoneWA ? 'checked' : ''} style="width:16px;height:16px;cursor:pointer">
            <span style="font-size:0.875rem">Este teléfono tiene WhatsApp</span>
          </label>
        </div>
        ${fieldInput('biz-email',        'Email de atención',    b.email, 'email')}
        ${fieldInput('biz-website',      'Sitio web',            b.website, 'url', 'https://')}
        ${fieldInput('biz-addressLine1', 'Dirección (ej: Cra 39 # 3-53, o Barrio)', b.addressLine1)}
        ${fieldInput('biz-addressLine2', 'Complemento / Referencia (opcional)', b.addressLine2)}
        ${fieldDeptSelect('biz-state', 'Departamento', dept)}
        ${fieldCitySelect('biz-city', 'Ciudad', dept, b.city)}
        ${fieldInput('biz-postalCode',   'Código postal (opcional)', b.postalCode)}
        <div class="detail-form-actions">
          <button type="submit" class="btn-primary btn-sm" id="nx-biz-save">Guardar</button>
          <button type="button" class="btn-ghost btn-sm" id="nx-biz-cancel">Cancelar</button>
          <span id="nx-biz-save-err" class="auth-error" hidden></span>
        </div>
      </form>`;
  }
  const emailLink = b.email
    ? `<a href="mailto:${esc(b.email)}" class="detail-link">${esc(b.email)}</a>` : null;
  const webLink = b.website
    ? `<a href="${esc(b.website)}" target="_blank" rel="noopener" class="detail-link">${esc(b.website)}</a>` : null;
  const location = [b.city, b.state].filter(Boolean).join(', ');
  const phoneDisplay = b.phone
    ? `<a href="tel:${esc(b.phone)}" class="detail-link">${esc(b.phone)}</a>${phoneWA ? ' <span title="Tiene WhatsApp" style="color:#25d366;font-size:.85em">📱</span>' : ''}`
    : null;
  return `
    <div class="detail-grid">
      ${fieldRO('Teléfono del negocio', phoneDisplay)}
      ${fieldRO('Email',               emailLink)}
      ${fieldRO('Sitio web',           webLink)}
      ${fieldRO('Dirección',           [b.addressLine1, b.addressLine2].filter(Boolean).join(', '))}
      ${fieldRO('Ciudad / Depto',      location || null)}
      ${fieldRO('Código postal',       esc(b.postalCode))}
    </div>`;
}

function buildPropietarioPanel(b, editing) {
  // Extrae strings seguros de metadata (maneja JObject → string)
  const ownerName    = metaStr(b, 'ownerName');
  const ownerCedula  = metaStr(b, 'ownerCedula');
  const ownerPhone   = metaStr(b, 'ownerPhone');
  const ownerEmail   = metaStr(b, 'ownerEmail');
  const ownerAddress = metaStr(b, 'ownerAddress');
  const ownerCity    = metaStr(b, 'ownerCity');
  const ownerState   = metaStr(b, 'ownerState');
  const docSoftware  = metaStr(b, 'docSoftware');
  const docEquipment = metaStr(b, 'docEquipment');
  const docPrivacy   = metaStr(b, 'docPrivacy');
  const docRut       = metaStr(b, 'docRut');
  const docCamara    = metaStr(b, 'docCamara');

  if (editing) {
    return `
      <form id="nx-biz-form" class="detail-grid detail-grid-edit">
        <div class="detail-section-title" style="grid-column:1/-1;font-weight:600;color:var(--text-muted);font-size:0.875rem;text-transform:uppercase;margin-top:0.25rem">Datos del propietario</div>
        ${fieldInput('biz-ownerName',    'Nombre completo',  ownerName,    'text')}
        ${fieldInput('biz-ownerCedula',  'Cédula',           ownerCedula,  'text')}
        ${fieldInput('biz-ownerPhone',   'Teléfono personal',ownerPhone,   'tel', '+57 300 000 0000')}
        ${fieldInput('biz-ownerEmail',   'Email personal',   ownerEmail,   'email')}
        ${fieldInput('biz-ownerAddress', 'Dirección',        ownerAddress)}
        ${fieldDeptSelect('biz-ownerState', 'Departamento', ownerState)}
        ${fieldCitySelect('biz-ownerCity', 'Ciudad', ownerState, ownerCity)}
        <div class="detail-section-title" style="grid-column:1/-1;font-weight:600;color:var(--text-muted);font-size:0.875rem;text-transform:uppercase;margin-top:1rem">Documentos</div>
        ${fieldInput('biz-docSoftware',    'URL firma uso de software',    docSoftware,    'url', 'https://')}
        ${fieldInput('biz-docEquipment',   'URL contrato equipos comodato',docEquipment,   'url', 'https://')}
        ${fieldInput('biz-docPrivacy',     'URL autorización datos pers.', docPrivacy,     'url', 'https://')}
        ${fieldInput('biz-docRut',         'URL RUT',                      docRut,         'url', 'https://')}
        ${fieldInput('biz-docCamara',      'URL Cámara de comercio',       docCamara,      'url', 'https://')}
        <div class="detail-form-actions">
          <button type="submit" class="btn-primary btn-sm" id="nx-biz-save">Guardar</button>
          <button type="button" class="btn-ghost btn-sm" id="nx-biz-cancel">Cancelar</button>
          <span id="nx-biz-save-err" class="auth-error" hidden></span>
        </div>
      </form>`;
  }
  return `
    <div class="detail-grid">
      <div class="detail-section-title" style="grid-column:1/-1;font-weight:600;color:var(--text-muted);font-size:0.875rem;text-transform:uppercase;margin-bottom:0.25rem">Propietario</div>
      ${fieldRO('Nombre',    esc(ownerName))}
      ${fieldRO('Cédula',    esc(ownerCedula))}
      ${fieldRO('Teléfono',  ownerPhone ? `<a href="tel:${esc(ownerPhone)}" class="detail-link">${esc(ownerPhone)}</a>` : null)}
      ${fieldRO('Email',     ownerEmail ? `<a href="mailto:${esc(ownerEmail)}" class="detail-link">${esc(ownerEmail)}</a>` : null)}
      ${fieldRO('Dirección', [ownerAddress, ownerCity, ownerState].filter(Boolean).join(', '))}
    </div>
    <div class="detail-grid" style="margin-top:1.25rem">
      <div class="detail-section-title" style="grid-column:1/-1;font-weight:600;color:var(--text-muted);font-size:0.875rem;text-transform:uppercase;margin-bottom:0.25rem">Documentos legales</div>
      ${fieldRO('Uso de software',  docBadge(docSoftware))}
      ${fieldRO('Contrato equipos', docBadge(docEquipment))}
      ${fieldRO('Autorización datos', docBadge(docPrivacy))}
      ${fieldRO('RUT',              docBadge(docRut))}
      ${fieldRO('Cámara de comercio', docBadge(docCamara))}
    </div>`;
}

function buildSuscripcionPanel(b) {
  return `
    <div class="detail-grid">
      <div class="detail-field">
        <span class="detail-label">Licencia</span>
        <span class="detail-value">${licenseBadge(b.licenseStatus)}</span>
      </div>
      ${fieldRO('Plan activo', esc(b.plan))}
      <div class="detail-field">
        <span class="detail-label">Estado empresa</span>
        <span class="detail-value">${bizStatusBadge(b.status)}</span>
      </div>
    </div>
    <div class="detail-subs-section">
      <div class="artifact-header" style="margin-top:1.5rem">
        <h3 style="font-size:var(--text-sm,0.875rem);margin:0">Historial de suscripciones</h3>
        <button class="btn-primary btn-sm" id="nx-sub-new-btn">+ Nueva</button>
      </div>
      <div id="nx-sub-new-form" hidden>
        <form id="nx-sub-form" class="inline-form inline-form-col" style="margin-top:0.75rem">
          <div class="form-row">
            <select id="sub-plan" class="form-input" required>
              ${_catalogo.planes.length
                ? _catalogo.planes.map(p => `<option value="${esc(p)}">${esc(p)}</option>`).join('')
                : '<option value="" disabled selected>Sin lista de planes</option>'}
            </select>
            <select id="sub-status" class="form-input form-input-sm">
              <option value="active">Activa</option>
              <option value="trial">Prueba</option>
              <option value="expired">Expirada</option>
              <option value="suspended">Suspendida</option>
              <option value="cancelled">Cancelada</option>
            </select>
          </div>
          <div class="form-row">
            <select id="sub-billing" class="form-input form-input-sm">
              <option value="monthly">Mensual</option>
              <option value="annual">Anual</option>
              <option value="one_time">Único</option>
            </select>
            <input type="number" id="sub-amount" class="form-input" placeholder="Monto" min="0" step="0.01">
            <input type="text"   id="sub-currency" class="form-input" placeholder="Moneda (COP)" maxlength="3">
          </div>
          <div class="form-row">
            <input type="date" id="sub-start" class="form-input" placeholder="Inicio">
            <input type="date" id="sub-end"   class="form-input" placeholder="Fin (opcional)">
          </div>
          <input type="text" id="sub-notes" class="form-input" placeholder="Notas (opcional)">
          <div class="form-row">
            <button type="submit" class="btn-primary btn-sm">Guardar</button>
            <button type="button" class="btn-ghost btn-sm" id="nx-sub-cancel-btn">Cancelar</button>
          </div>
          <span id="nx-sub-form-err" class="auth-error" hidden></span>
        </form>
      </div>
      <div class="artifact-list" id="nx-subs-list">
        <p class="empty-state">Cargando suscripciones...</p>
      </div>
    </div>

    <div class="detail-subs-section" style="margin-top:1.5rem">
      <div class="artifact-header">
        <h3 style="font-size:var(--text-sm,0.875rem);margin:0">&#128187; Cliente Nexo Desktop</h3>
      </div>
      <div id="nx-desktop-client-body">
        <p class="empty-state">Cargando...</p>
      </div>
    </div>`;
}

async function loadSuscripciones() {
  const $list = document.getElementById('nx-subs-list');
  if (!$list) return;

  const result = await SubscriptionApiClient.list(businessId);
  const subs   = result.ok ? (result.data?.data || result.data || []) : [];

  const STATUS_LABELS  = { active: 'Activa', trial: 'Prueba', expired: 'Expirada', suspended: 'Suspendida', cancelled: 'Cancelada' };
  const STATUS_COLORS  = { active: '#238636', trial: '#d29922', expired: '#f85149', suspended: '#6e7681', cancelled: '#6e7681' };
  const CYCLE_LABELS   = { monthly: 'mensual', annual: 'anual', one_time: 'único' };

  if (!result.ok) {
    $list.innerHTML = `<p class="empty-state">Error: ${result.error}</p>`;
    return;
  }

  if (subs.length === 0) {
    $list.innerHTML = '<p class="empty-state">Sin suscripciones registradas</p>';
    return;
  }

  const today = new Date();
  $list.innerHTML = subs.map((s, i) => {
    const color = STATUS_COLORS[s.status] || '#6e7681';
    const label = STATUS_LABELS[s.status] || s.status;
    const cycle = CYCLE_LABELS[s.billingCycle] || s.billingCycle;
    const endDate = s.endDate ? new Date(s.endDate.length === 10 ? s.endDate + 'T12:00:00' : s.endDate) : null;
    const graceDate = endDate ? new Date(endDate.getTime() + 5 * 24 * 60 * 60 * 1000) : null;
    const endLabel = endDate ? formatearFecha(endDate, { day: '2-digit', month: 'short', year: 'numeric' }) : '∞';
    // PANEL-WIZARD-PARIDAD-NEGOCIO-001: el modo de vencimiento (calculado por duracion vs
    // fecha exacta) lo captura el wizard pero solo quedaba implicito en notes. Lo elevamos
    // a chip visible de 1a clase (derivado de notes; un campo estructurado requeriria columna backend).
    const vencMode = subVencMode(s.notes);
    const graceLabel = graceDate ? formatearFecha(graceDate, { day: '2-digit', month: 'short', year: 'numeric' }) : null;
    const isExpiringSoon = endDate && endDate > today && (endDate - today) < 7 * 24 * 60 * 60 * 1000;
    const isInGrace = endDate && endDate < today && graceDate && graceDate > today;
    const statusColor = isInGrace ? '#d29922' : isExpiringSoon ? '#d29922' : color;
    const sid = esc(s.subscriptionId || s.subscription_id || s.id) || `sub-${i}`;
    const SUB_STATUS_OPTS = ['active','trial','expired','suspended','cancelled']
      .map(st => `<option value="${st}"${s.status===st?' selected':''}>${STATUS_LABELS[st]||st}</option>`).join('');
    const BILLING_OPTS = [['monthly','Mensual'],['annual','Anual'],['one_time','Único pago']]
      .map(([v,l]) => `<option value="${v}"${s.billingCycle===v?' selected':''}>${l}</option>`).join('');
    return `
      <div class="artifact-item" data-sub-id="${sid}">
        <div class="task-info">
          <div class="artifact-item-title">${esc(s.plan)}</div>
          <div class="artifact-item-meta">
            Inicio: ${fmt(s.startDate)}
            &middot; <strong>Vence: ${endLabel}</strong>
            ${vencMode ? `<span title="${esc(vencMode.title)}" style="margin-left:6px;background:${vencMode.color}22;color:${vencMode.color};border-radius:8px;padding:1px 7px;font-size:0.875rem;font-weight:600">${esc(vencMode.label)}</span>` : ''}
            ${graceLabel ? `&middot; Gracia hasta: ${graceLabel}` : ''}
            &middot; ${cycle}
          </div>
          ${isInGrace ? `<div class="artifact-item-meta" style="color:#d29922;font-weight:600">⚠ En período de gracia — vence el ${graceLabel}</div>` : ''}
          ${s.notes ? `<div class="artifact-item-meta" style="font-style:italic">${esc(s.notes)}</div>` : ''}
        </div>
        <div class="task-actions">
          <span class="status-badge" style="background:${statusColor}22;color:${statusColor}">${label}</span>
          <button class="btn-ghost btn-sm" data-edit-sub="${sid}" title="Editar">✏</button>
          <button class="btn-icon btn-delete" data-del-sub="${sid}" title="Eliminar">&times;</button>
        </div>
      </div>
      <div class="sub-edit-panel" id="sub-edit-${sid}" hidden>
        <form class="sub-edit-form" data-sub-edit-form="${sid}">
          <div class="form-row-3">
            <div><label class="detail-label">Plan</label>
              <input class="form-input" name="plan" value="${esc(s.plan)}" required></div>
            <div><label class="detail-label">Estado</label>
              <select class="form-input" name="status">${SUB_STATUS_OPTS}</select></div>
            <div><label class="detail-label">Ciclo</label>
              <select class="form-input" name="billingCycle">${BILLING_OPTS}</select></div>
          </div>
          <div class="form-row-3">
            <div><label class="detail-label">Inicio</label>
              <input type="date" class="form-input" name="startDate" value="${isoToDate(s.startDate)}"></div>
            <div><label class="detail-label">Vencimiento</label>
              <input type="date" class="form-input" name="endDate" value="${isoToDate(s.endDate)}"></div>
            <div><label class="detail-label">Monto</label>
              <input type="number" class="form-input" name="amount" value="${s.amount||0}" min="0"></div>
          </div>
          <div><label class="detail-label">Notas</label>
            <input class="form-input" name="notes" value="${esc(s.notes||'')}"></div>
          <div class="form-actions-row">
            <button type="submit" class="btn-primary btn-sm">Guardar</button>
            <button type="button" class="btn-ghost btn-sm" data-cancel-sub-edit="${sid}">Cancelar</button>
            <span class="sub-edit-err auth-error" hidden></span>
          </div>
        </form>
      </div>`;
  }).join('');
}

/** Genera la clave interna (empresa) a partir del nombre del negocio. */
function autoEmpresaKey(displayName) {
  return (displayName || '')
    .toUpperCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '-')
    .replace(/[^A-Z0-9\-]/g, '')
    .slice(0, 40);
}

/** Valida que una fecha ISO sea real (no el min-date de SQL = 0001-01-01). */
function isRealDate(iso) {
  if (!iso) return false;
  const d = new Date(iso);
  return !isNaN(d.getTime()) && d.getFullYear() >= 2000;
}

async function loadDesktopClient() {
  const $body = document.getElementById('nx-desktop-client-body');
  if (!$body) return;

  // === LEGACY (read-only) ===
  // El registro manual de "Cliente Desktop" fue reemplazado por el sistema de
  // validacion automatica de licencia v2 (3 capas anti-fraude: fecha apertura
  // monotonica, codigo mensual rotativo, comparacion server time).
  // Esta seccion solo MUESTRA el cliente legacy si existe (read-only),
  // pero ya no permite editar ni crear nuevos.
  // Las maquinas reales se registran automaticamente en license_machines al
  // hacer ValidarAlIniciar() desde el Desktop.

  const res     = await LicenseApiClient.listClients();
  const clients = res.ok ? (res.data?.data ?? res.data ?? []) : [];
  const list    = Array.isArray(clients) ? clients : [];

  const client = list.find(c => (c.business_id || c.businessId) === businessId) ?? null;

  if (!client) {
    $body.innerHTML = `
      <div style="padding:.75rem;background:#161b22;border:1px solid #30363d;border-radius:6px;color:#8b949e;font-size:0.875rem">
        <div style="margin-bottom:.3rem"><strong style="color:#c9d1d9">Validacion automatica</strong></div>
        Las maquinas se registran automaticamente al primer arranque del Desktop.
        No requiere configuracion manual aqui.
      </div>`;
    return;
  }

  // Cliente legacy existente: mostrar como informativo
  const pcVal     = client.pc_name     ?? client.pcName     ?? '';
  const folderVal = client.folder_path ?? client.folderPath ?? '';
  const notesVal  = client.notes       ?? '';
  const subLimit  = client.subscription_limit ?? client.subscriptionLimit ?? '';

  let expiryChip = '';
  if (isRealDate(subLimit)) {
    const d = new Date(subLimit.length === 10 ? subLimit + 'T12:00:00' : subLimit);
    const expired = d < new Date();
    expiryChip = `<span style="color:${expired?'#f85149':'#238636'};font-weight:600">${formatearFecha(d, {day:'2-digit',month:'short',year:'numeric'})}</span>`;
  }

  $body.innerHTML = `
    <div style="display:flex;align-items:flex-start;gap:1rem;flex-wrap:wrap;padding:0.75rem;background:#161b22;border:1px solid #30363d;border-radius:6px">
      <span style="font-size:1.1rem;margin-top:2px;opacity:.6">📋</span>
      <div style="flex:1;min-width:0">
        <div style="font-weight:600;color:#8b949e;margin-bottom:.3rem;font-size:0.875rem;text-transform:uppercase;letter-spacing:.05em">
          Cliente Desktop legacy <span style="font-weight:400;text-transform:none;font-size:0.875rem;color:#6e7681">(read-only — reemplazado por validacion v2)</span>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:.75rem 1.5rem;font-size:0.875rem;color:#c9d1d9">
          <span><span style="color:#6e7681">Empresa:</span> <code style="font-size:0.875rem">${esc(client.empresa)}</code></span>
          ${pcVal     ? `<span><span style="color:#6e7681">PC:</span> <strong>${esc(pcVal)}</strong></span>` : ''}
          ${folderVal ? `<span><span style="color:#6e7681">Carpeta:</span> <code style="font-size:0.875rem">${esc(folderVal)}</code></span>` : ''}
          ${expiryChip ? `<span><span style="color:#6e7681">Vence:</span> ${expiryChip}</span>` : ''}
          ${notesVal  ? `<span style="color:#6e7681;font-style:italic">${esc(notesVal)}</span>` : ''}
        </div>
      </div>
    </div>`;
}

const EQUIPO_TIPOS  = ['Computador','Tablet','Caja registradora','Impresora','Terminal POS','Celular','TV/Monitor','Router/Modem','Otro'];
const EQUIPO_ESTADOS = ['activo','en_mantenimiento','dado_de_baja'];
const EQUIPO_ESTADO_LABELS = { activo: 'Activo', en_mantenimiento: 'En mantenimiento', dado_de_baja: 'Dado de baja' };
const EQUIPO_ESTADO_COLORS = { activo: '#238636', en_mantenimiento: '#d29922', dado_de_baja: '#f85149' };

// Estado en memoria del tab Equipos (cache + filtros activos para vista legacy read-only)
let _equiposCache    = [];
let _equiposFilters  = { search: '', tipo: '', estado: '' };

function _equiposApplyFilters(list, filters) {
  const q = String(filters.search || '').toLowerCase().trim();
  return list.filter(eq => {
    if (filters.tipo   && (eq.tipo   || '') !== filters.tipo)   return false;
    if (filters.estado && (eq.estado || '') !== filters.estado) return false;
    if (q) {
      const hay = [eq.nombre, eq.marca, eq.tipo, eq.serial, eq.notas]
        .map(v => String(v || '').toLowerCase()).join(' ');
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

function _equiposCountByState(list) {
  const counts = { activo: 0, en_mantenimiento: 0, dado_de_baja: 0, total: list.length };
  for (const e of list) {
    const k = e.estado || 'activo';
    counts[k] = (counts[k] || 0) + 1;
  }
  return counts;
}

function _equiposExportCsv(list, bizName) {
  const headers = ['Nombre','Marca','Tipo','Serial','Estado','En uso desde','Notas'];
  const escCsv = v => {
    const s = String(v ?? '');
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = list.map(e => [
    e.nombre,
    e.marca,
    e.tipo,
    e.serial,
    EQUIPO_ESTADO_LABELS[e.estado] || e.estado || '',
    e.assignedSince ? formatearFecha(e.assignedSince) : '',
    e.notas,
  ].map(escCsv).join(','));
  const csv  = [headers.join(','), ...rows].join('\n');
  // BOM para compatibilidad con Excel es-CO
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  const safe = String(bizName || 'negocio').replace(/[^\w\-]+/g, '_').slice(0, 40);
  a.href     = url;
  a.download = `equipos_${safe}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function _equiposRenderList() {
  const $list = document.getElementById('nx-equipos-list');
  if (!$list) return;

  const filtered = _equiposApplyFilters(_equiposCache, _equiposFilters);
  // Orden estable: por nombre asc. Los "dado_de_baja" se muestran al final.
  filtered.sort((a, b) => {
    const stateRank = s => (s === 'dado_de_baja' ? 1 : 0);
    const sr = stateRank(a.estado) - stateRank(b.estado);
    if (sr !== 0) return sr;
    return String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es', { sensitivity: 'base' });
  });

  const $count = document.getElementById('nx-equipos-count');
  if ($count) {
    $count.textContent = _equiposCache.length === filtered.length
      ? `${_equiposCache.length} equipo(s) legacy`
      : `${filtered.length} de ${_equiposCache.length} equipo(s) legacy`;
  }

  if (filtered.length === 0) {
    $list.innerHTML = _equiposCache.length === 0
      ? '' // vacio: lo resuelve el empty-state global en loadEquipos
      : '<p class="empty-state">No hay equipos que coincidan con los filtros.</p>';
    return;
  }

  // Vista de SOLO LECTURA. El CRUD de equipos vive en #/equipos (catalogo global).
  $list.innerHTML = filtered.map(eq => {
    const estColor = EQUIPO_ESTADO_COLORS[eq.estado] || '#6e7681';
    const estLabel = EQUIPO_ESTADO_LABELS[eq.estado] || eq.estado;
    // EQUIPOS-PANEL-T1: de quien es, de donde vino y si alguien lo vio (casillas de EQUIPOS-API-T1, solo lectura).
    const prop = propietarioDe(eq);
    const ver = verificadoDe(eq);
    const origen = origenDe(eq);
    const colorVer = ver.estado === 'si' ? '#3fb950' : ver.estado === 'no' ? '#d29922' : '#6e7681';
    return `
      <div class="artifact-item" data-equipo-id="${esc(eq.id)}">
        <div class="task-info">
          <div class="artifact-item-title">
            ${esc(eq.nombre)}
            ${eq.marca ? `<span style="font-weight:400;color:var(--color-text-muted)"> — ${esc(eq.marca)}</span>` : ''}
          </div>
          <div class="artifact-item-meta">
            ${eq.tipo ? `${esc(eq.tipo)} · ` : ''}
            ${eq.serial ? `S/N: ${esc(eq.serial)} · ` : ''}
            ${eq.assignedSince ? `En uso desde: ${fmt(eq.assignedSince)}` : ''}
            ${eq.notas ? ` · ${esc(eq.notas)}` : ''}
          </div>
          <div class="artifact-item-meta" data-casillas-equipo style="margin-top:2px">
            <span data-propietario="${prop.clave}">${esc(prop.etiqueta)}</span>
            · <span data-verificado="${ver.estado}" style="color:${colorVer}">${esc(ver.texto)}</span>
            ${origen ? ` · <span style="color:#8b949e">${esc(origen)}</span>` : ''}
            ${ver.estado === 'sin_dato' ? '' : `<button type="button" class="btn-ghost btn-sm" style="margin-left:6px;padding:1px 8px;font-size:0.875rem"
              data-marcar-verificado="${esc(eq.id)}" data-valor="${ver.estado === 'si' ? 'false' : 'true'}">${ver.estado === 'si' ? 'Quitar verificacion' : 'Marcar verificado'}</button>`}
          </div>
        </div>
        <div class="task-actions">
          <span class="status-badge" style="background:${estColor}22;color:${estColor}">${estLabel}</span>
        </div>
      </div>`;
  }).join('');
}

function _equiposRenderToolbar() {
  const counts = _equiposCountByState(_equiposCache);
  const tipoOpts = ['<option value="">Todos los tipos</option>']
    .concat(EQUIPO_TIPOS.map(t => `<option value="${esc(t)}"${_equiposFilters.tipo === t ? ' selected' : ''}>${esc(t)}</option>`))
    .join('');
  const estadoOpts = ['<option value="">Todos los estados</option>']
    .concat(EQUIPO_ESTADOS.map(s => `<option value="${s}"${_equiposFilters.estado === s ? ' selected' : ''}>${EQUIPO_ESTADO_LABELS[s]}</option>`))
    .join('');

  const chip = (label, color, n) =>
    `<span class="status-badge" style="background:${color}22;color:${color}">${label}: ${n}</span>`;

  return `
    <div class="detail-equipos-toolbar" style="display:flex;flex-wrap:wrap;gap:8px;align-items:center;margin-bottom:10px">
      <input id="nx-equipo-search" class="form-input" type="search"
             placeholder="Buscar nombre, marca, serial, notas..."
             value="${esc(_equiposFilters.search)}"
             style="flex:1 1 220px;min-width:180px">
      <select id="nx-equipo-filter-tipo" class="form-input" style="flex:0 1 180px">${tipoOpts}</select>
      <select id="nx-equipo-filter-estado" class="form-input" style="flex:0 1 180px">${estadoOpts}</select>
      <button id="nx-equipo-export-btn" class="btn-ghost btn-sm" title="Exportar CSV"
              ${_equiposCache.length === 0 ? 'disabled' : ''}>⭳ CSV</button>
    </div>
    <div style="display:flex;flex-wrap:wrap;gap:6px;align-items:center;margin-bottom:12px;font-size:14px;color:var(--color-text-muted)">
      <span id="nx-equipos-count">${counts.total} equipo(s) legacy</span>
      <span style="flex:1"></span>
      ${chip('Activos', EQUIPO_ESTADO_COLORS.activo, counts.activo || 0)}
      ${chip('Mantenim.', EQUIPO_ESTADO_COLORS.en_mantenimiento, counts.en_mantenimiento || 0)}
      ${chip('De baja', EQUIPO_ESTADO_COLORS.dado_de_baja, counts.dado_de_baja || 0)}
    </div>`;
}

/**
 * EQUIPOS-PANEL-T1 parte 1: el consentimiento de equipos del NEGOCIO y el conteo de verificados y de quien son.
 * Solo lectura: el PUT de equipos no cambia estas casillas (EQUIPOS-API-T1). Reglas en application/equipos/casillasEquipo.js.
 */
function _equiposCasillas(equipos) {
  const c = consentimientoDelNegocio(equipos);
  const r = resumenDeCasillas(equipos);
  const colorC = c.estado === 'firmado' ? '#3fb950' : c.estado === 'no' ? '#f85149' : '#6e7681';
  return `
    <div data-casillas-negocio="${c.estado}" style="display:flex;flex-wrap:wrap;gap:8px 16px;align-items:center;margin:0 0 10px;padding:8px 12px;background:#0d1117;border:1px solid #30363d;border-radius:6px;font-size:0.875rem">
      <strong style="color:${colorC}">${esc(c.texto)}</strong>
      ${r.conCasillas
        ? `<span>${r.verificados} de ${r.total} verificado${r.total === 1 ? '' : 's'} fisicamente</span>
           <span style="color:#8b949e">${r.deNexo} de Nexo · ${r.delNegocio} del negocio${r.sinPropietario ? ` · ${r.sinPropietario} sin propietario` : ''}</span>
           ${c.estado === 'firmado'
             ? '<button type="button" class="btn-ghost btn-sm" data-consentimiento="quitar" style="padding:1px 8px;font-size:0.875rem">Quitar firma</button>'
             : `<span data-consentimiento-firmar style="display:inline-flex;gap:6px;align-items:center">
                  <input type="text" class="form-input" data-consentimiento-version placeholder="version (ej. v1)" style="width:110px;padding:2px 6px;font-size:0.875rem">
                  <button type="button" class="btn-primary btn-sm" data-consentimiento="firmar" style="padding:1px 8px;font-size:0.875rem">Marcar firmado</button>
                </span>`}
           <span data-marca-error class="auth-error" style="font-size:0.875rem"></span>`
        : '<span style="color:#8b949e">La API aun no trae propietario ni verificacion (EQUIPOS-API-T1 sin publicar).</span>'}
    </div>`;
}

async function loadEquipos() {
  const $panel = document.getElementById('nx-biz-panel');
  if (!$panel || currentTab !== 'equipos') return;

  const result  = await EquiposApiClient.list(businessId);
  const equipos = result.ok ? (result.data?.data || result.data || []) : [];
  _equiposCache = equipos;

  const hasLegacy = result.ok && equipos.length > 0;

  $panel.innerHTML = `
    <div class="detail-equipos">

      <div style="background:#1c2128;border:1px solid #30363d;border-radius:6px;padding:10px 14px;margin-bottom:12px;display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap">
        <span style="font-size:0.875rem;color:#8b949e">
          Para crear, editar o eliminar equipos usa el
          <a href="#/equipos" style="color:#58a6ff">Catalogo Global de Equipos</a>.
        </span>
        <a href="#/equipos" class="btn-ghost btn-sm">Ir al catalogo &rarr;</a>
      </div>

      ${!result.ok
        ? `<p class="empty-state" style="color:#f85149">Error al cargar equipos: ${esc(result.error || 'Revisa la conexión con la API')}</p>`
        : hasLegacy
          ? `
            <h3 style="margin:0 0 6px;font-size:1rem">Equipos del negocio (solo lectura)</h3>
            <p style="font-size:0.875rem;color:#8b949e;margin:0 0 10px">
              Equipos registrados en este negocio (incluye los importados del link v10).
            </p>
            ${_equiposCasillas(equipos)}
            ${_equiposRenderToolbar()}
            <div class="artifact-list" id="nx-equipos-list"></div>`
          : ''}

      <hr style="margin:18px 0;border:0;border-top:1px solid #30363d">

      <div id="nx-equipos-catalog-wrap">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px">
          <div>
            <h3 style="margin:0;font-size:1rem">Catalogo de equipos asignados</h3>
            <p style="font-size:0.875rem;color:#8b949e;margin:2px 0 0">
              Asignaciones via <a href="#/equipos">catalogo global</a>
            </p>
          </div>
          <div style="display:flex;gap:6px">
            <button class="btn-ghost btn-sm" id="nx-biz-cat-history-btn">Historial</button>
            <button class="btn-primary btn-sm" id="nx-biz-cat-assign-btn"${accionDeMaqueta(EquiposCatalogApiClient.ES_MAQUETA)}>+ Asignar equipo</button>
          </div>
        </div>
        ${avisoDeMaqueta(EquiposCatalogApiClient.ES_MAQUETA)}
        <div id="nx-biz-cat-assign-form" hidden></div>
        <div id="nx-biz-cat-active" class="artifact-list"><p class="empty-state">Cargando asignaciones...</p></div>
        <div id="nx-biz-cat-history" hidden style="margin-top:10px"></div>
      </div>
    </div>`;

  if (!result.ok) return;
  if (hasLegacy) _equiposRenderList();
  _loadCatalogAssignments($panel);

  // EQUIPOS-PANEL-T1 resto: marcar verificado y el consentimiento (rutas de A, solo admin). Tras cada cambio se relee la
  // lista: la API pone fecha y quien, el panel no las inventa.
  const $marcas = $panel.querySelector('.detail-equipos');
  if ($marcas) $marcas.onclick = async (ev) => {
    const $err = $panel.querySelector('[data-marca-error]');
    const ver = ev.target.closest('[data-marcar-verificado]');
    const con = ev.target.closest('[data-consentimiento]');
    if (!ver && !con) return;
    let res;
    (ver || con).disabled = true;
    if (ver) {
      res = await EquiposApiClient.marcarVerificacion(businessId, ver.dataset.marcarVerificado, ver.dataset.valor === 'true');
    } else {
      const firmar = con.dataset.consentimiento === 'firmar';
      const c = cuerpoDeConsentimiento(firmar, $panel.querySelector('[data-consentimiento-version]')?.value);
      if (!c.ok) { if ($err) $err.textContent = c.error; con.disabled = false; return; }
      res = await EquiposApiClient.marcarConsentimiento(businessId, c.cuerpo);
    }
    const m = motivoDeMarca(res);
    if (m.hecho) { await loadEquipos(); return; }
    if ($err) $err.textContent = m.error; else showStatus(m.error, true);
    (ver || con).disabled = false;
  };

  if (hasLegacy) {
    // Toolbar: busqueda (input = live)
    document.getElementById('nx-equipo-search')?.addEventListener('input', (ev) => {
      _equiposFilters.search = ev.target.value || '';
      _equiposRenderList();
    });
    document.getElementById('nx-equipo-filter-tipo')?.addEventListener('change', (ev) => {
      _equiposFilters.tipo = ev.target.value || '';
      _equiposRenderList();
    });
    document.getElementById('nx-equipo-filter-estado')?.addEventListener('change', (ev) => {
      _equiposFilters.estado = ev.target.value || '';
      _equiposRenderList();
    });
    document.getElementById('nx-equipo-export-btn')?.addEventListener('click', () => {
      const filtered = _equiposApplyFilters(_equiposCache, _equiposFilters);
      const src = filtered.length > 0 ? filtered : _equiposCache;
      if (src.length === 0) { showStatus('No hay equipos para exportar'); return; }
      _equiposExportCsv(src, currentBiz?.displayName || currentBiz?.legalName);
      showStatus(`Exportados ${src.length} equipo(s) a CSV`);
    });
  }
}

// ---------------------------------------------------------------------------
// PANEL-OPS-V3-001 T4 — Instalaciones Desktop del negocio
// ---------------------------------------------------------------------------

function _fmtRelative(isoStr) {
  if (!isoStr) return '—';
  const d = new Date(isoStr);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  if (diff < 0) return 'en el futuro';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'hace unos segundos';
  const min = Math.floor(sec / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} ${h === 1 ? 'hora' : 'horas'}`;
  const days = Math.floor(h / 24);
  if (days < 30) return `hace ${days} ${days === 1 ? 'día' : 'días'}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `hace ${months} ${months === 1 ? 'mes' : 'meses'}`;
  return `hace ${Math.floor(months / 12)} años`;
}

function _installStatus(lastSeen) {
  if (!lastSeen) return { key: 'offline', label: 'Offline' };
  const d = new Date(lastSeen);
  if (isNaN(d.getTime())) return { key: 'offline', label: 'Offline' };
  const diffMin = (Date.now() - d.getTime()) / 60000;
  if (diffMin < 5) return { key: 'online', label: 'Online' };
  if (diffMin < 60 * 24) return { key: 'idle', label: 'Idle' };
  return { key: 'offline', label: 'Offline' };
}

async function loadInstalaciones() {
  const $panel = document.getElementById('nx-biz-panel');
  if (!$panel || currentTab !== 'instalaciones') return;

  const plan = currentBiz?.plan || '';
  const limit = planInstallLimit(plan);

  const res = await LicenseApiClient.listDesktopInstallations(businessId);
  let rows = [];
  let mock = false;

  if (res.ok) {
    rows = res.data?.data || res.data || [];
    if (!Array.isArray(rows)) rows = [];
  } else {
    // NEEDS-API fallback: mock con 2 filas
    mock = true;
    const nowIso = new Date().toISOString();
    const hoursAgo = (h) => new Date(Date.now() - h * 3600 * 1000).toISOString();
    rows = [
      {
        machine_fingerprint: 'mock-fp-0001-abcdef01234567',
        machine_name: 'PC-CAJA-01 (mock)',
        last_seen: hoursAgo(1),
        version: '2.2.84',
        token_valid_until: new Date(Date.now() + 30 * 86400000).toISOString(),
        active: true
      },
      {
        machine_fingerprint: 'mock-fp-0002-fedcba76543210',
        machine_name: 'PC-COCINA-01 (mock)',
        last_seen: hoursAgo(48),
        version: '2.2.84',
        token_valid_until: new Date(Date.now() + 30 * 86400000).toISOString(),
        active: true
      }
    ];
  }

  const activeCount = rows.filter(r => r.active !== false).length;
  const excedido = activeCount > limit;

  let bannerClass = 'install-banner--ok';
  let bannerLabel = `${activeCount} de ${limit} instalaciones permitidas · Plan: ${esc(plan || '—')}`;
  if (excedido) {
    bannerClass = 'install-banner--error';
    bannerLabel = `Límite excedido: ${activeCount}/${limit} instalaciones · Plan: ${esc(plan || '—')}`;
  }

  const mockBanner = mock
    ? `<div class="install-banner install-banner--warn">API mock · pendiente backend (needs-api: <code>GET /api/v1/businesses/{id}/desktop-installations</code>)</div>`
    : '';

  const tableRows = rows.map(r => {
    const fp = String(r.machine_fingerprint || r.fingerprint || '');
    const fpShort = fp.slice(0, 8);
    const name = r.machine_name || r.pc_name || '(sin nombre)';
    const last = r.last_seen || r.lastSeen;
    const version = r.version || r.version_app || '—';
    const status = _installStatus(last);
    const active = r.active !== false;
    let revokeBtn;
    if (!active) {
      revokeBtn = `<span style="color:#8b949e;font-size:0.875rem">Revocada</span>`;
    } else if (mock) {
      // PANEL-OPS-V4-001 T4 — mock no accionable
      revokeBtn = `<span class="install-mock-chip" title="API mock — boton deshabilitado hasta que exista el endpoint">mock · no accionable</span>`;
    } else {
      revokeBtn = `<button class="install-revoke-btn" data-fp="${esc(fp)}" data-name="${esc(name)}">Revocar</button>`;
    }
    return `
      <tr>
        <td>${esc(name)}</td>
        <td><span class="install-fingerprint" title="${esc(fp)}">${esc(fpShort)}</span></td>
        <td>${esc(_fmtRelative(last))}</td>
        <td>${esc(version)}</td>
        <td><span class="install-status install-status--${status.key}">${status.label}</span></td>
        <td>${revokeBtn}</td>
      </tr>`;
  }).join('');

  $panel.innerHTML = `
    <div class="detail-instalaciones">
      <div id="nx-web-token-block"></div>
      ${mockBanner}
      <div class="install-banner ${bannerClass}">
        <span>${bannerLabel}</span>
        <button class="btn-ghost btn-sm" id="nx-install-refresh">&#8635; Actualizar</button>
      </div>
      ${rows.length === 0
        ? '<p class="empty-state">No hay instalaciones Desktop registradas para este negocio.</p>'
        : `<div style="overflow-x:auto">
            <table class="install-table">
              <thead>
                <tr>
                  <th>Maquina</th>
                  <th>Fingerprint</th>
                  <th>Ultima conexion</th>
                  <th>Version</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>${tableRows}</tbody>
            </table>
          </div>`}
    </div>`;

  document.getElementById('nx-install-refresh')?.addEventListener('click', () => loadInstalaciones());
  $panel.querySelectorAll('.install-revoke-btn').forEach(btn => {
    btn.addEventListener('click', () => _confirmRevokeInstall(btn.dataset.fp, btn.dataset.name, mock));
  });

  // WEB-TOKEN-PER-BUSINESS-V1-001 T3: bloque de conexion/sincronizacion (web token +
  // toggle sync_enabled + regenerar). Se hidrata async para no bloquear la tabla.
  loadWebTokenBlock();
}

/**
 * WEB-TOKEN-PER-BUSINESS-V1-001 T3 — Bloque "Conexion y sincronizacion" dentro de la
 * pestana Instalaciones. Muestra el web token persistido por negocio (para soporte),
 * un toggle de sincronizacion (sync_enabled = control de desconexion) y un boton para
 * regenerar el token.
 *
 * Consume LicenseApiClient.getBusinessSetup/setSyncEnabled/regenerateBusinessToken.
 * Degradacion segura: si el endpoint (Argy-A T1) aun no esta LIVE, muestra estado
 * "pendiente backend" con un token mock enmascarado y los controles deshabilitados.
 */
async function loadWebTokenBlock() {
  const $block = document.getElementById('nx-web-token-block');
  if (!$block || currentTab !== 'instalaciones') return;

  $block.innerHTML = `<div class="webtoken-card webtoken-card--loading">Cargando configuracion de sincronizacion...</div>`;

  let setup = null;
  let mock = false;
  try {
    const res = await LicenseApiClient.getBusinessSetup(businessId);
    if (res.ok) {
      const d = res.data?.data || res.data || {};
      setup = {
        webToken: d.webToken ?? d.web_token ?? '',
        syncEnabled: (d.syncEnabled ?? d.sync_enabled) !== false,
        tokenGeneratedAt: d.tokenGeneratedAt ?? d.token_generated_at ?? null
      };
    } else {
      mock = true;
    }
  } catch {
    mock = true;
  }

  if (mock || !setup) {
    // NEEDS-API fallback: el endpoint business-setup (Argy-A T1) aun no responde.
    setup = {
      webToken: 'mock-webtoken-' + String(businessId || '').slice(0, 12) + '-PENDIENTE',
      syncEnabled: true,
      tokenGeneratedAt: null
    };
    mock = true;
  }

  renderWebTokenBlock($block, setup, mock);
}

/**
 * Pinta el bloque de web token. tokenShown controla si el token esta visible o
 * enmascarado (toggle ojito). isMock deshabilita las acciones (backend pendiente).
 */
function renderWebTokenBlock($block, setup, isMock, tokenShown = false) {
  const token = String(setup.webToken || '');
  const masked = token ? token.slice(0, 6) + '••••••••••••' + token.slice(-4) : '(sin token)';
  const display = tokenShown ? token : masked;
  const genAt = setup.tokenGeneratedAt
    ? formatearFecha(setup.tokenGeneratedAt, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }, { parte: 'todo' })
    : null;

  const syncOn = setup.syncEnabled !== false;
  const syncColor = syncOn ? '#3fb950' : '#d29922';
  const syncLabel = syncOn ? 'Sincronizacion activa' : 'Sincronizacion pausada';
  const syncDesc = syncOn
    ? 'El Desktop de este negocio puede sincronizar datos con la nube.'
    : 'El Desktop NO podra sincronizar hasta reactivar. Util para desconectar un negocio moroso o en mantenimiento.';

  const mockBanner = isMock
    ? `<div class="install-banner install-banner--warn" style="margin-bottom:10px">API mock · pendiente backend (needs-api: <code>GET /api/v1/license/business-setup/{id}</code> — Argy-A T1)</div>`
    : '';

  const disabledAttr = isMock ? 'disabled' : '';
  const disabledTitle = isMock ? 'title="Pendiente backend (Argy-A T1)"' : '';

  $block.innerHTML = `
    <div class="webtoken-card" style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:16px;margin-bottom:14px">
      ${mockBanner}
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-bottom:12px">
        <div>
          <h3 style="margin:0;font-size:0.95rem;color:#f0f6fc">Conexion y sincronizacion</h3>
          <p style="margin:4px 0 0;font-size:0.875rem;color:#8b949e">Token de sincronizacion del negocio (para soporte) y control de conexion del Desktop con la nube.</p>
        </div>
        <span style="display:inline-flex;align-items:center;gap:6px;font-size:0.875rem;color:${syncColor};font-weight:600;white-space:nowrap">
          <span style="width:8px;height:8px;border-radius:50%;background:${syncColor};display:inline-block"></span>${syncLabel}
        </span>
      </div>

      <div style="display:grid;grid-template-columns:90px 1fr;gap:8px 12px;align-items:center;font-size:0.875rem">
        <label style="color:#8b949e">Web token</label>
        <div style="display:flex;gap:6px;align-items:center;flex-wrap:wrap">
          <code id="nx-webtoken-value" style="flex:1;min-width:180px;padding:6px 10px;background:#0d1117;border:1px solid #30363d;border-radius:5px;color:#c9d1d9;font-size:0.875rem;font-family:monospace;word-break:break-all">${esc(display)}</code>
          <button class="btn-ghost btn-sm" id="nx-webtoken-toggle" title="${tokenShown ? 'Ocultar' : 'Mostrar'} token">${tokenShown ? '🙈' : '👁'}</button>
          <button class="btn-ghost btn-sm" id="nx-webtoken-copy" title="Copiar token al portapapeles">📋 Copiar</button>
        </div>

        ${genAt ? `
          <label style="color:#8b949e">Generado</label>
          <span style="color:#8b949e;font-size:0.875rem">${esc(genAt)}</span>
        ` : ''}
      </div>

      <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;flex-wrap:wrap;margin-top:14px;padding-top:12px;border-top:1px solid #21262d">
        <div style="flex:1;min-width:200px">
          <label style="display:inline-flex;align-items:center;gap:8px;cursor:${isMock ? 'not-allowed' : 'pointer'};font-size:0.875rem;color:#c9d1d9">
            <input type="checkbox" id="nx-webtoken-sync" ${syncOn ? 'checked' : ''} ${disabledAttr} ${disabledTitle} style="width:16px;height:16px;cursor:${isMock ? 'not-allowed' : 'pointer'}">
            Sincronizacion habilitada
          </label>
          <p style="margin:4px 0 0;font-size:0.875rem;color:#6e7681">${syncDesc}</p>
        </div>
        <button class="btn-ghost btn-sm" id="nx-webtoken-regen" ${disabledAttr} ${disabledTitle} style="color:#d29922;border-color:#d2992255;white-space:nowrap">&#8635; Regenerar token</button>
      </div>

      <p id="nx-webtoken-status" style="margin:10px 0 0;font-size:0.875rem;min-height:1em"></p>
    </div>`;

  // Toggle mostrar/ocultar token
  document.getElementById('nx-webtoken-toggle')?.addEventListener('click', () => {
    renderWebTokenBlock($block, setup, isMock, !tokenShown);
  });

  // Copiar al portapapeles
  document.getElementById('nx-webtoken-copy')?.addEventListener('click', async () => {
    const $st = document.getElementById('nx-webtoken-status');
    try {
      await navigator.clipboard.writeText(token);
      if ($st) { $st.style.color = '#3fb950'; $st.textContent = '✓ Token copiado al portapapeles.'; }
    } catch {
      if ($st) { $st.style.color = '#d29922'; $st.textContent = 'No se pudo copiar automaticamente. Selecciona y copia manual.'; }
    }
    setTimeout(() => { const s = document.getElementById('nx-webtoken-status'); if (s) s.textContent = ''; }, 2500);
  });

  // Toggle sync_enabled
  document.getElementById('nx-webtoken-sync')?.addEventListener('change', async (e) => {
    const $st = document.getElementById('nx-webtoken-status');
    const wanted = e.target.checked;
    if (isMock) {
      if ($st) { $st.style.color = '#d29922'; $st.textContent = 'Pendiente backend (Argy-A T2): no se persiste el toggle todavia.'; }
      return;
    }
    e.target.disabled = true;
    if ($st) { $st.style.color = '#8b949e'; $st.textContent = 'Guardando...'; }
    const res = await LicenseApiClient.setSyncEnabled(businessId, wanted);
    if (res.ok) {
      setup.syncEnabled = wanted;
      renderWebTokenBlock($block, setup, isMock, false);
      const s2 = document.getElementById('nx-webtoken-status');
      if (s2) { s2.style.color = '#3fb950'; s2.textContent = wanted ? '✓ Sincronizacion activada.' : '✓ Sincronizacion pausada.'; }
    } else {
      e.target.checked = !wanted; // revertir
      e.target.disabled = false;
      if ($st) { $st.style.color = '#f85149'; $st.textContent = 'Error: ' + (res.error || 'no se pudo guardar'); }
    }
  });

  // Regenerar token (con confirmacion modal custom)
  document.getElementById('nx-webtoken-regen')?.addEventListener('click', () => {
    if (isMock) {
      const $st = document.getElementById('nx-webtoken-status');
      if ($st) { $st.style.color = '#d29922'; $st.textContent = 'Pendiente backend (Argy-A T2): regenerar no disponible todavia.'; }
      return;
    }
    _confirmRegenerateToken($block, setup, isMock);
  });
}

/**
 * Modal de confirmacion para regenerar el web token. Regenerar invalida el token
 * anterior: el Desktop debe reconfigurarse con el nuevo. Decision consciente.
 */
function _confirmRegenerateToken($block, setup, isMock) {
  const $modal = document.createElement('div');
  $modal.className = 'modal-backdrop';
  $modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px';
  $modal.innerHTML = `
    <div style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:20px;max-width:440px;width:100%">
      <h3 style="margin:0 0 10px;color:#c9d1d9">Regenerar web token</h3>
      <p style="color:#c9d1d9;font-size:0.875rem;margin:0 0 8px">
        El token actual quedara <strong style="color:#d29922">invalido</strong> de inmediato.
      </p>
      <p style="color:#8b949e;font-size:0.875rem;margin:0 0 16px">
        El Desktop de este negocio dejara de sincronizar hasta que se reconfigure con el nuevo token (primer arranque o config de sincronizacion).
      </p>
      <div style="display:flex;gap:8px;justify-content:flex-end">
        <button class="btn-ghost btn-sm" id="nx-regen-cancel">Cancelar</button>
        <button class="btn-primary btn-sm" id="nx-regen-confirm" style="background:#d29922;border-color:#d29922">Regenerar</button>
      </div>
    </div>`;
  document.body.appendChild($modal);
  const close = () => { document.body.removeChild($modal); };
  $modal.querySelector('#nx-regen-cancel').addEventListener('click', close);
  $modal.addEventListener('click', (e) => { if (e.target === $modal) close(); });
  $modal.querySelector('#nx-regen-confirm').addEventListener('click', async () => {
    const $btn = $modal.querySelector('#nx-regen-confirm');
    $btn.disabled = true;
    $btn.textContent = 'Regenerando…';
    const res = await LicenseApiClient.regenerateBusinessToken(businessId);
    close();
    const $st = document.getElementById('nx-webtoken-status');
    if (res.ok) {
      const d = res.data?.data || res.data || {};
      setup.webToken = d.webToken ?? d.web_token ?? setup.webToken;
      setup.tokenGeneratedAt = d.tokenGeneratedAt ?? d.token_generated_at ?? new Date().toISOString();
      renderWebTokenBlock($block, setup, isMock, true); // mostrar el nuevo token
      const s2 = document.getElementById('nx-webtoken-status');
      if (s2) { s2.style.color = '#3fb950'; s2.textContent = '✓ Token regenerado. Reconfigura el Desktop con el nuevo token.'; }
    } else {
      if ($st) { $st.style.color = '#f85149'; $st.textContent = 'Error al regenerar: ' + (res.error || 'intenta de nuevo'); }
    }
  });
}

function _confirmRevokeInstall(fingerprint, machineName, isMock) {
  const $modal = document.createElement('div');
  $modal.className = 'modal-backdrop';
  $modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:1000;display:flex;align-items:center;justify-content:center;padding:16px';
  $modal.innerHTML = `
    <div style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:20px;max-width:420px;width:100%">
      <h3 style="margin:0 0 10px;color:#c9d1d9">Revocar instalacion</h3>
      <p style="color:#c9d1d9;font-size:0.875rem;margin:0 0 8px">
        ¿Confirmas revocar el acceso de <strong>${esc(machineName)}</strong>?
      </p>
      <p style="color:#8b949e;font-size:0.875rem;margin:0 0 16px">
        La próxima vez que esta máquina intente sincronizar será rechazada.
      </p>
      <div style="display:flex;gap:8px;justify-content:flex-end">
        <button class="btn-ghost btn-sm" id="nx-install-cancel">Cancelar</button>
        <button class="btn-primary btn-sm" id="nx-install-confirm" style="background:#da3633;border-color:#da3633">Revocar</button>
      </div>
    </div>`;
  document.body.appendChild($modal);
  const close = () => { document.body.removeChild($modal); };
  $modal.querySelector('#nx-install-cancel').addEventListener('click', close);
  $modal.addEventListener('click', (e) => { if (e.target === $modal) close(); });
  $modal.querySelector('#nx-install-confirm').addEventListener('click', async () => {
    const $btn = $modal.querySelector('#nx-install-confirm');
    $btn.disabled = true;
    $btn.textContent = 'Revocando…';
    if (isMock) {
      // NEEDS-API: endpoint aun no existe, solo confirma UX
      avisar('La revocacion todavia no esta publicada en la API: esto es una simulacion.');
      close();
      return;
    }
    const res = await LicenseApiClient.revokeDesktopInstallation(businessId, fingerprint);
    if (res.ok) {
      close();
      loadInstalaciones();
    } else {
      $btn.disabled = false;
      $btn.textContent = 'Revocar';
      avisar('No se pudo revocar: ' + (res.error || 'intenta de nuevo'), { tono: 'error' });
    }
  });
}

// ---------------------------------------------------------------------------
// Catalogo de equipos (asignaciones del nuevo modelo 2-niveles)
// ---------------------------------------------------------------------------
const _CATALOG_STATUS_COLORS = { asignado: '#1f6feb', disponible: '#238636', en_mantenimiento: '#d29922', dado_de_baja: '#f85149' };

async function _loadCatalogAssignments($panel) {
  const $active = $panel.querySelector('#nx-biz-cat-active');
  if (!$active) return;

  const res = await EquiposCatalogApiClient.getBusinessEquipment(businessId);
  if (!res.ok) {
    $active.innerHTML = `<p class="empty-state" style="color:#f85149">Error catalogo: ${esc(res.error)}</p>`;
    return;
  }
  const items = res.data || [];
  if (items.length === 0) {
    $active.innerHTML = _equiposCache.length === 0
      ? '<p class="empty-state">No hay equipos asignados a este negocio. Asigna desde el <a href="#/equipos">catalogo global</a>.</p>'
      : '<p class="empty-state">Sin equipos del catalogo asignados.</p>';
  } else {
    $active.innerHTML = items.map(({ assignment, unit, model }) => {
      const color = _CATALOG_STATUS_COLORS.asignado;
      return `
        <div class="artifact-item" data-cat-asg-id="${esc(assignment.id)}">
          <div class="task-info">
            <div class="artifact-item-title">
              ${esc(model?.name || 'Modelo desconocido')}
              <span style="font-weight:400;color:var(--color-text-muted)"> &middot; S/N ${esc(unit?.serial || '—')}</span>
            </div>
            <div class="artifact-item-meta">
              ${model?.brand ? esc(model.brand) + ' · ' : ''}
              Asignado: ${fmt(assignment.assigned_at)}
              ${assignment.assigned_by ? ` · por ${esc(assignment.assigned_by)}` : ''}
              ${assignment.notes ? ` · ${esc(assignment.notes)}` : ''}
            </div>
          </div>
          <div class="task-actions">
            <span class="status-badge" style="background:${color}22;color:${color}">Asignado</span>
            <button class="btn-ghost btn-sm" data-cat-return="${esc(assignment.id)}"${accionDeMaqueta(EquiposCatalogApiClient.ES_MAQUETA)}>Devolver</button>
          </div>
        </div>`;
    }).join('');
  }

  // Bind botones de la seccion catalogo (una vez por render)
  $panel.querySelector('#nx-biz-cat-assign-btn')?.addEventListener('click', () => _openAssignForm($panel));
  $panel.querySelector('#nx-biz-cat-history-btn')?.addEventListener('click', () => _toggleCatalogHistory($panel));

  $active.querySelectorAll('[data-cat-return]').forEach(btn => {
    btn.addEventListener('click', async (ev) => {
      // El boton se guarda antes de esperar: tras un await, ev.currentTarget ya es null.
      const $btn = ev.currentTarget;
      const aid = $btn.dataset.catReturn;
      if (!await confirmar({ titulo: 'Marcar el equipo como devuelto', aceptar: 'Marcar devuelto' })) return;
      $btn.disabled = true;
      const rr = await EquiposCatalogApiClient.returnUnit(aid);
      if (rr.ok) { showStatus('Equipo devuelto'); await _loadCatalogAssignments($panel); }
      else { showStatus(`Error: ${rr.error}`, true); $btn.disabled = false; }
    });
  });
}

async function _openAssignForm($panel) {
  const $wrap = $panel.querySelector('#nx-biz-cat-assign-form');
  if (!$wrap) return;
  if (!$wrap.hidden) { $wrap.hidden = true; return; }
  $wrap.hidden = false;
  $wrap.innerHTML = '<p class="empty-state">Cargando unidades disponibles...</p>';

  const res = await EquiposCatalogApiClient.listAvailableUnits();
  if (!res.ok) { $wrap.innerHTML = `<p class="empty-state" style="color:#f85149">Error: ${esc(res.error)}</p>`; return; }
  const available = res.data || [];
  if (available.length === 0) {
    $wrap.innerHTML = '<p class="empty-state">No hay unidades disponibles en el catalogo. Crea unidades en <a href="#/equipos">#/equipos</a>.</p>';
    return;
  }

  const opts = available.map(({ unit, model }) =>
    `<option value="${esc(unit.id)}">${esc(model?.name || 'Modelo')} · S/N ${esc(unit.serial)}${model?.brand ? ' · ' + esc(model.brand) : ''}</option>`
  ).join('');

  $wrap.innerHTML = `
    <form id="nx-biz-cat-assign-f" class="sub-edit-form" style="margin-bottom:10px">
      <div class="form-row-3">
        <div style="grid-column: span 2"><label class="detail-label">Unidad disponible *</label>
          <select class="form-input" name="unit_id" required>
            <option value="">Seleccionar...</option>${opts}
          </select></div>
        <div><label class="detail-label">Notas</label>
          <input class="form-input" name="notes" placeholder="Observaciones de la asignacion"></div>
      </div>
      <div class="form-actions-row">
        <button type="submit" class="btn-primary btn-sm">Asignar</button>
        <button type="button" class="btn-ghost btn-sm" id="nx-biz-cat-assign-cancel">Cancelar</button>
        <span class="sub-edit-err auth-error" hidden></span>
      </div>
    </form>`;

  $wrap.querySelector('#nx-biz-cat-assign-cancel').addEventListener('click', () => { $wrap.hidden = true; });
  $wrap.querySelector('#nx-biz-cat-assign-f').addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const fd = new FormData(ev.target);
    const $btn = ev.target.querySelector('[type=submit]');
    const $err = ev.target.querySelector('.sub-edit-err');
    $btn.disabled = true;
    const rr = await EquiposCatalogApiClient.assignUnit(
      fd.get('unit_id'),
      businessId,
      { notes: fd.get('notes') || null, assigned_by: 'panel' }
    );
    if (rr.ok) {
      showStatus('Equipo asignado');
      $wrap.hidden = true;
      await _loadCatalogAssignments($panel);
    } else {
      $err.textContent = rr.error; $err.hidden = false; $btn.disabled = false;
    }
  });
}

async function _toggleCatalogHistory($panel) {
  const $h = $panel.querySelector('#nx-biz-cat-history');
  if (!$h) return;
  if (!$h.hidden) { $h.hidden = true; return; }
  $h.hidden = false;
  $h.innerHTML = '<p class="empty-state">Cargando historial...</p>';
  const res = await EquiposCatalogApiClient.getBusinessHistory(businessId, 50);
  if (!res.ok) { $h.innerHTML = `<p class="empty-state" style="color:#f85149">Error: ${esc(res.error)}</p>`; return; }
  const hist = res.data || [];
  if (hist.length === 0) { $h.innerHTML = '<p class="empty-state">Sin historial de asignaciones previas.</p>'; return; }
  $h.innerHTML = `
    <h4 style="margin:0 0 6px;font-size:0.9rem;color:#c9d1d9">Historial de asignaciones devueltas</h4>
    <div class="artifact-list">
      ${hist.map(({ assignment, unit, model }) => `
        <div class="artifact-item">
          <div class="task-info">
            <div class="artifact-item-title">
              ${esc(model?.name || 'Modelo')} · S/N ${esc(unit?.serial || '—')}
            </div>
            <div class="artifact-item-meta">
              ${fmt(assignment.assigned_at)} &rarr; ${fmt(assignment.returned_at)}
              ${assignment.notes ? ' · ' + esc(assignment.notes) : ''}
            </div>
          </div>
        </div>`).join('')}
    </div>`;
}

function collectFormData() {
  const val  = id => { const el = document.getElementById(id); return el ? (el.value?.trim() || null) : undefined; };
  const valO = id => { const v = val(id); return v === undefined ? undefined : v; };

  if (currentTab === 'info') {
    const startDateVal = document.getElementById('biz-startDate')?.value;
    return {
      displayName:  valO('biz-displayName'),
      legalName:    valO('biz-legalName'),
      taxId:        valO('biz-taxId'),
      businessType: valO('biz-businessType'),
      plan:         valO('biz-plan'),
      startDate:    startDateVal ? new Date(startDateVal).toISOString() : undefined,
    };
  }

  if (currentTab === 'contacto') {
    const $waCheck = document.getElementById('biz-phoneHasWhatsapp');
    return {
      phone:              valO('biz-phone'),
      phoneHasWhatsapp:   $waCheck ? $waCheck.checked : true,
      email:              valO('biz-email'),
      website:            valO('biz-website'),
      addressLine1:       valO('biz-addressLine1'),
      addressLine2:       valO('biz-addressLine2'),
      city:               valO('biz-city'),
      state:              valO('biz-state'),
      postalCode:         valO('biz-postalCode'),
      country:            'Colombia',
    };
  }

  if (currentTab === 'propietario') {
    const existing = currentBiz?.metadata || {};
    return {
      metadata: {
        ...existing,
        ownerName:    valO('biz-ownerName'),
        ownerCedula:  valO('biz-ownerCedula'),
        ownerPhone:   valO('biz-ownerPhone'),
        ownerEmail:   valO('biz-ownerEmail'),
        ownerAddress: valO('biz-ownerAddress'),
        ownerCity:    valO('biz-ownerCity'),
        ownerState:   valO('biz-ownerState'),
        docSoftware:  valO('biz-docSoftware'),
        docEquipment: valO('biz-docEquipment'),
        docPrivacy:   valO('biz-docPrivacy'),
        docRut:       valO('biz-docRut'),
        docCamara:    valO('biz-docCamara'),
      },
    };
  }

  return {};
}

/**
 * EL ENLACE DE LA INVITACION, EN LA FICHA (ONB-FICHA-MOVIL-T1).
 * Tres acciones, cada una con SU clic: Abrir (un enlace de verdad, no window.open), Copiar el
 * mensaje y Enviar por WhatsApp. Antes se copiaba y se abria WhatsApp con window.open DESPUES
 * del await: en el celular el navegador lo bloquea por no venir de un clic, y Josse se quedaba
 * sin nada en pantalla. Ahora el enlace queda visible y seleccionable.
 */
function pintarInvitacion(url, mensaje) {
  const $caja = document.getElementById('nx-invitacion-link');
  if (!$caja) { showStatus(`Invitación lista: ${url}`); return; }
  const wa = `https://wa.me/?text=${encodeURIComponent(mensaje)}`;
  $caja.innerHTML = `
    <div style="font-size:0.875rem;color:#3fb950;font-weight:600;margin-bottom:6px">✅ Invitación generada</div>
    <code data-invitacion-url style="display:block;font-size:0.875rem;color:#c9d1d9;word-break:break-all;user-select:all;margin-bottom:8px">${esc(url)}</code>
    <div style="display:flex;gap:8px;flex-wrap:wrap">
      <a href="${esc(url)}" target="_blank" rel="noopener" class="btn-primary btn-sm" style="font-size:0.875rem;text-decoration:none">Abrir</a>
      <button type="button" class="btn-ghost btn-sm" data-invitacion-copiar style="font-size:0.875rem">Copiar mensaje</button>
      <a href="${esc(wa)}" target="_blank" rel="noopener" class="btn-ghost btn-sm" style="font-size:0.875rem;text-decoration:none">Enviar por WhatsApp</a>
    </div>`;
  $caja.hidden = false;
  $caja.querySelector('[data-invitacion-copiar]')?.addEventListener('click', async (ev) => {
    try {
      await navigator.clipboard.writeText(mensaje);
      ev.target.textContent = 'Copiado';
    } catch {
      // Sin permiso de portapapeles: el enlace igual esta a la vista y se puede seleccionar.
      ev.target.textContent = 'Copia el enlace de arriba';
    }
  });
}

function showStatus(msg, isError = false) {
  const $s = document.getElementById('nx-biz-status');
  if (!$s) return;
  $s.textContent = msg;
  $s.hidden = false;
  $s.style.color = isError ? '#f85149' : '#238636';
  setTimeout(() => { $s.hidden = true; }, 3000);
}

function switchTab(tab) {
  currentTab = tab;
  const $panel = document.getElementById('nx-biz-panel');
  if (!$panel) return;

  document.querySelectorAll('.detail-tab').forEach(b => {
    b.classList.toggle('active', b.dataset.tab === tab);
  });

  $panel.innerHTML = buildPanel(tab, currentBiz, false);
  if (tab === 'info')       loadMarca();
  if (tab === 'equipos')    loadEquipos();
  if (tab === 'instalaciones') loadInstalaciones();
  if (tab === 'suscripcion') { loadSuscripciones(); loadDesktopClient(); }
  if (tab === 'apariencia') refreshAparienciaFondoCacheHint();
}

function bindDeptChangeListeners(panel) {
  const deptSelects = panel.querySelectorAll('[data-dept-select]');
  deptSelects.forEach(deptSel => {
    const isOwner = deptSel.id === 'biz-ownerState';
    const cityId  = isOwner ? 'biz-ownerCity' : 'biz-city';
    deptSel.addEventListener('change', () => {
      const dept   = deptSel.value;
      const cities = ciudadesDe(dept);
      const $city  = document.getElementById(cityId);
      if (!$city) return;
      if (cities.length) {
        const sel = document.createElement('select');
        sel.id        = cityId;
        sel.className = 'form-input';
        // Departamento nuevo: la ciudad arranca vacia; no se escribe la primera de la lista.
        sel.innerHTML = opcionesDelSelect(['', ...cities], '');
        $city.replaceWith(sel);
      } else {
        const inp = document.createElement('input');
        inp.type  = 'text';
        inp.id    = cityId;
        inp.className = 'form-input';
        $city.replaceWith(inp);
      }
    });
  });
}

const N8N_FONDO_WEBHOOK        = 'https://n8n.nexodigital360.com/webhook/generar-fondo-negocio';
// El webhook mejorar-fondo se retira (FONDO-PANEL-T1 paso 2, punto 3): el flujo v2 entrega la imagen lista.

// URL directa del logo Nexo (dl.dropboxusercontent.com evita la página HTML de Dropbox)
const NEXO_WATERMARK_URL = 'https://dl.dropboxusercontent.com/scl/fi/pu5d3r3zcf9qc62au4gez/nexo-logo-modificado-fondo-negro.jpg?rlkey=04hub3szey6messi3py443p1t&raw=1';

/**
 * @param displaySrc URL corta o blob: para <img> (evita inyectar megabytes en atributos de botones)
 * @param fondoRef id en fondoPreviewRefStore si isB64; vacío si URL remota
 */
/** La linea de abajo con el tamano REAL de la imagen (cuanto se recorta depende de su proporcion). */
function actualizarResumenDelSalon($card) {
  const $img = $card?.querySelector('[data-fondo-img]');
  const $res = $card?.querySelector('[data-fondo-resumen]');
  if (!$img || !$res) return;
  const modo = $card.querySelector('.nx-fondo-marco-modo')?.value || 'restaurante';
  const w = $img.naturalWidth || 16;
  const h = $img.naturalHeight || 9;
  $res.textContent = resumenDelSalon(w, h, modo);
}

/** Las piezas solidas del salon encima de la propuesta, en porcentajes del rectangulo del salon. */
function piezasDelSalonHTML(modo) {
  const pct = (n) => `${(n * 100).toFixed(2)}%`;
  return `<div data-fondo-marco data-modo="${esc(modo)}" style="position:absolute;inset:0;pointer-events:none">`
    + piezasQueTapan(modo).map((p) => `<div data-fondo-pieza="${esc(p.clave)}" title="${esc(p.titulo)}"
        style="position:absolute;left:${pct(p.x)};top:${pct(p.y)};width:${pct(p.ancho)};height:${pct(p.alto)};background:rgba(255,255,255,.82);border:1px solid rgba(48,54,61,.6);border-radius:3px;box-sizing:border-box"></div>`).join('')
    + '</div>';
}

function buildFondoCardHTML(label, model, displaySrc, fondoRef, isB64, error) {
  const cardStyle = 'flex:1;min-width:240px;border:1px solid #30363d;border-radius:8px;padding:12px;background:#161b22;display:flex;flex-direction:column;gap:8px';
  if (error) {
    return `<div style="${cardStyle}">
      <p style="font-size:0.875rem;font-weight:600;color:#8b949e;margin:0">${esc(label)}</p>
      <p style="font-size:0.875rem;color:#f85149;margin:0">⚠ ${esc(error)}</p>
      <p style="font-size:0.875rem;color:#6e7681;margin:0">${esc(model)}</p>
    </div>`;
  }

  const isB64Attr = isB64 ? 'true' : 'false';
  const safeLabel = label.toLowerCase().replace(/\s/g, '_');
  const refAttr   = isB64 && fondoRef ? ` data-fondo-ref="${esc(fondoRef)}"` : '';
  const srcAttr   = !isB64 && displaySrc ? ` data-src="${esc(displaySrc)}"` : '';

  // FONDO-PANEL-T1 punto 2 (Ares #25944): la propuesta se ve COMO EN EL SALON del Desktop: el
  // rectangulo del salon (1159x621) con la imagen cubriendo y centrada, y encima las piezas
  // solidas que la tapan. Medido en la captura real de D (marcoDelSalon.js), no en la tarea.
  return `<div data-fondo-card style="${cardStyle}">
    <p style="font-size:0.875rem;font-weight:600;color:#c9d1d9;margin:0">${esc(label)}</p>
    <div data-fondo-salon style="position:relative;width:100%;aspect-ratio:${SALON.ancho}/${SALON.alto};border-radius:5px;overflow:hidden;background:#000;min-height:100px">
      <img src="${esc(displaySrc)}" alt="${esc(label)}" data-fondo-img
        style="width:100%;height:100%;object-fit:cover;object-position:center;display:block">
      ${piezasDelSalonHTML('restaurante')}
    </div>
    <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;font-size:0.875rem;color:#8b949e">
      <label style="display:flex;align-items:center;gap:5px;cursor:pointer">
        <input type="checkbox" class="nx-fondo-marco-toggle" checked> Ver como lo tapa el salon del Desktop</label>
      <label for="nx-fondo-modo-${esc(safeLabel)}">Pantalla</label>
      <select id="nx-fondo-modo-${esc(safeLabel)}" class="nx-fondo-marco-modo" style="font-size:0.875rem;padding:2px 4px;background:#0d1117;color:#c9d1d9;border:1px solid #30363d;border-radius:4px">
        <option value="restaurante">Restaurante (salon)</option><option value="mostrador">Almacen (mostrador)</option></select>
    </div>
    <p data-fondo-resumen style="font-size:0.875rem;color:#8b949e;margin:0">${esc(resumenDelSalon(16, 9, 'restaurante'))}</p>
    <p style="font-size:0.875rem;color:#6e7681;margin:0">${esc(model)}</p>
    <div style="display:flex;gap:6px;flex-wrap:wrap">
      <button type="button" class="nx-fondo-view-btn btn-ghost btn-sm"${refAttr}${srcAttr} data-is-b64="${isB64Attr}" style="font-size:0.875rem">🔍 Ampliar</button>
      <button type="button" class="nx-fondo-dl-btn btn-ghost btn-sm"${refAttr}${srcAttr} data-is-b64="${isB64Attr}" data-label="${esc(safeLabel)}" style="font-size:0.875rem">⬇ Descargar</button>
      <button type="button" class="nx-fondo-save-btn btn-primary btn-sm"${refAttr}${srcAttr} style="font-size:0.875rem">💾 Usar este fondo</button>
    </div>
  </div>`;
}

/**
 * Construye el HTML de las tarjetas de variantes a partir del JSON del webhook (n8n).
 * @returns {{ ok: true, html: string } | { ok: false, html: string, error: string }}
 */
function buildFondoResultInnerHtml(data) {
  const isV3 = data.v1 !== undefined || data.v2 !== undefined || data.v3 !== undefined;

  let html = `<p class="detail-label" style="margin-bottom:10px">${isV3 ? 'Selecciona la variación que prefieras' : 'La propuesta'}</p>
      <div style="display:flex;gap:12px;flex-wrap:wrap">`;

  if (isV3) {
    const rows = [
      { label: 'Variante 1 – Clásico', d: data.v1 },
      { label: 'Variante 2 – Ornamental', d: data.v2 },
      { label: 'Variante 3 – Premium', d: data.v3 },
    ];
    for (const { label, d } of rows) {
      const b64 = d?.b64 || null;
      const err = normalizeWebhookError(d?.error) || (!b64 ? 'Sin respuesta' : null);
      if (b64 && !err) {
        const { refId, previewSrc } = registerFondoB64Src(b64);
        html += buildFondoCardHTML(label, d?.model || 'Gemini Pro', previewSrc, refId, true, null);
      } else {
        html += buildFondoCardHTML(label, d?.model || 'Gemini Pro', '', '', true, err);
      }
    }
  } else {
    const imageUrl = data.image_url || data.url || data.fondo_url || data.gemini?.b64 || '';
    if (!imageUrl) {
      return { ok: false, html: '', error: 'El workflow no devolvió ninguna imagen.' };
    }
    const isB64 = isDataImageUrl(imageUrl);
    if (isB64) {
      const { refId, previewSrc } = registerFondoB64Src(imageUrl);
      html += buildFondoCardHTML('Fondo generado', 'IA', previewSrc, refId, true, null);
    } else {
      html += buildFondoCardHTML('Fondo generado', 'IA', imageUrl, '', false, null);
    }
  }

  // FONDO-PANEL-T1 paso 2 (puntos 1 y 6): Otra propuesta es la MISMA llamada al flujo; reemplaza lo de arriba.
  html += `</div>
      <div style="display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-top:12px">
        <button type="button" id="nx-otra-propuesta-btn" class="btn-ghost btn-sm" style="font-size:0.875rem">🔄 Otra propuesta</button>
        <span style="font-size:0.875rem;color:#8b949e">Genera una nueva y reemplaza la de arriba.</span>
      </div>
      <p style="font-size:0.875rem;color:#6e7681;margin-top:10px">
        Los prompts del flujo se leen desde el catálogo n8n (<code>INS-FONDO-001</code>). Puedes revisarlos y editarlos en <strong>Instrucciones</strong> del panel.
      </p>`;

  return { ok: true, html };
}

/**
 * Pinta el panel de resultados y registra blobs de vista previa (tras revoke previo).
 */
function injectFondoWebhookResult(data) {
  const $result = document.getElementById('nx-fondo-result');
  const $inner  = document.getElementById('nx-fondo-result-inner');
  if (!$result || !$inner) return { ok: false, error: 'Panel de resultados no disponible.' };
  revokeFondoPreviewBlobs();
  const built = buildFondoResultInnerHtml(data);
  if (!built.ok) return { ok: false, error: built.error };
  $inner.innerHTML = built.html;
  $result.hidden = false;
  return { ok: true };
}

async function syncFondoCacheHint() {
  const hint = document.getElementById('nx-fondo-cache-hint');
  const text = document.getElementById('nx-fondo-cache-hint-text');
  if (!hint || !text || !businessId) return;
  const entry = await loadFondoWebhookPayload(businessId);
  if (!entry || !isFondoCacheEntryFresh(entry)) {
    hint.hidden = true;
    return;
  }
  const when = formatearFecha(entry.savedAt, { dateStyle: 'short', timeStyle: 'short' }, { parte: 'todo' });
  text.textContent =
    `Hay una generación reciente guardada en este dispositivo (${when}). ` +
    'Si el móvil bloqueó la pantalla o se cerró la pestaña mientras esperabas, puedes recuperar las variantes aquí.';
  hint.hidden = false;
}

function refreshAparienciaFondoCacheHint() {
  if (currentTab !== 'apariencia') return;
  setTimeout(() => { void syncFondoCacheHint(); }, 0);
}

/**
 * SUBIR EL LOGO COMO ARCHIVO (FONDO-PANEL-T1 paso 1, COORD #16139).
 *
 * El archivo viaja al endpoint de apariencia y lo que queda guardado en la ficha es la URL
 * que devuelve el API. El campo URL sigue existiendo: quien ya tenga un enlace no pierde nada,
 * y la subida solo lo reemplaza. Guardar sigue siendo del boton Guardar de la ficha; esto solo
 * deja la direccion lista en el campo, y lo dice en pantalla para que nadie se vaya sin guardar.
 */
async function handleSubirLogo($input) {
  const $estado = document.getElementById('ap-logo-archivo-estado');
  const $url    = document.getElementById('ap-logoUrl');
  const $prev   = document.getElementById('ap-logo-preview');
  const archivo = $input?.files?.[0];
  if (!archivo) return;

  const decir = (texto, color) => {
    if ($estado) {
      $estado.textContent = texto;
      $estado.style.color = color;
    }
  };

  $input.disabled = true;
  decir('Subiendo la imagen…', '#8b949e');

  // La firma del archivo, que es lo que el API mira: un .png renombrado no es un PNG.
  // FONDO-PANEL-T1 punto 7: con el mismo comienzo del archivo se mira si trae transparencia.
  let primerosBytes = null;
  let aviso = null;
  try {
    const comienzo = new Uint8Array(await archivo.slice(0, 65536).arrayBuffer());
    primerosBytes = comienzo.subarray(0, 8);
    aviso = avisoDeTransparencia(transparenciaDelLogo(comienzo));
  } catch { /* sin bytes, que decida el API */ }

  const res = await AparienciaApiClient.subirArchivo(businessId, 'logo', archivo, primerosBytes);
  $input.disabled = false;

  if (!res.ok) {
    decir(res.error, '#f85149');
    $input.value = '';
    return;
  }

  if ($url) $url.value = res.url;
  if ($prev) { $prev.src = res.url; $prev.style.display = ''; }
  if (aviso) decir('Imagen subida. ' + aviso + ' Si la dejas, dale a Guardar para que quede en la ficha.', '#d29922');
  else decir('Imagen subida. Falta darle a Guardar para que quede en la ficha.', '#3fb950');
}

/**
 * SUBIR EL FONDO COMO ARCHIVO (FONDO-SUBIR-IMAGEN-T1, Josse 17-oct). Igual que el logo: el archivo va al endpoint de
 * apariencia (tipo fondo) y en el campo queda la URL que devuelve el API; Guardar la deja en la ficha. Si la imagen no
 * es 16:9 se avisa (el Desktop la recorta o deja bandas), no se bloquea. Si habia un fondo de IA, lo reemplaza.
 */
async function handleSubirFondo($input) {
  const $estado = document.getElementById('ap-fondo-archivo-estado');
  const $prev   = document.getElementById('ap-fondo-preview');
  const archivo = $input?.files?.[0];
  if (!archivo) return;

  const decir = (texto, color) => {
    if ($estado) { $estado.textContent = texto; $estado.style.color = color; }
  };

  let comienzo = null;
  try { comienzo = new Uint8Array(await archivo.slice(0, 65536).arrayBuffer()); } catch { /* que decida el API */ }
  const noSePuede = porQueNoSePuedeSubir(archivo, 'fondo', comienzo ? comienzo.subarray(0, 8) : null);
  if (noSePuede) { decir(noSePuede, '#f85149'); $input.value = ''; return; }
  const aviso = comienzo ? avisoDeProporcion(medidasDeLaImagen(comienzo)) : null;

  $input.disabled = true;
  decir('Subiendo la imagen…', '#8b949e');
  const res = await AparienciaApiClient.subirArchivo(businessId, 'fondo', archivo, comienzo ? comienzo.subarray(0, 8) : null);
  $input.disabled = false;

  if (!res.ok) { decir(res.error, '#f85149'); $input.value = ''; return; }

  // El fondo de IA guarda su URL en un campo oculto; la subida lo reemplaza y quita su tarjeta.
  document.getElementById('ap-fondo-ia-preview')?.remove();
  const $url = document.getElementById('ap-fondoUrl');
  if ($url) $url.value = res.url;
  if ($prev) { $prev.src = res.url; $prev.style.display = ''; }
  if (aviso) decir('Imagen subida. ' + aviso + ' Si la dejas, dale a Guardar para que quede en la ficha.', '#d29922');
  else decir('Imagen subida. Falta darle a Guardar para que quede en la ficha.', '#3fb950');
}

async function handleGenerarFondo() {
  const $btn    = document.getElementById('nx-generar-fondo-btn');
  const $result = document.getElementById('nx-fondo-result');
  const $inner  = document.getElementById('nx-fondo-result-inner');

  if (!$btn || !$result || !$inner) return;

  const cfg     = acfg(currentBiz);
  const logoUrl = cfg.logo_url || '';
  if (!logoUrl) {
    showStatus('Primero guarda el logo del negocio en Apariencia → Editar', true);
    return;
  }

  $btn.disabled    = true;
  $btn.textContent = '⏳ Generando una propuesta…';
  const $otra = document.getElementById('nx-otra-propuesta-btn');
  if ($otra) { $otra.disabled = true; $otra.textContent = '⏳ Generando…'; }
  $result.hidden   = true;
  revokeFondoPreviewBlobs();
  $inner.innerHTML = '';

  let wakeLock = null;
  try {
    try {
      if (navigator.wakeLock?.request) {
        wakeLock = await navigator.wakeLock.request('screen');
      }
    } catch { /* permisos o no soportado */ }

    const payload = {
      business_id:      businessId,
      business_name:    currentBiz.displayName || '',
      business_type:    currentBiz.businessType || '',
      logo_url:         logoUrl,
      nexo_logo_url:    NEXO_WATERMARK_URL || '',
      color_primario:   cfg.color_primario   || '',
      color_secundario: cfg.color_secundario || '',
      color_texto:      cfg.color_texto      || '',
    };

    const res = await fetchQueCambia(N8N_FONDO_WEBHOOK, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify(payload),
    });

    if (!res.ok) {
      const errText = await res.text().catch(() => `HTTP ${res.status}`);
      throw new Error(errText || `HTTP ${res.status}`);
    }

    const data = await res.json();
    const injected = injectFondoWebhookResult(data);
    if (!injected.ok) throw new Error(injected.error);

    await saveFondoWebhookPayload(businessId, data);
    refreshAparienciaFondoCacheHint();
  } catch (err) {
    showStatus(`Error generando fondo: ${err.message}`, true);
  } finally {
    try {
      await wakeLock?.release?.();
    } catch { /* */ }
    wakeLock = null;
    $btn.disabled    = false;
    $btn.textContent = '✨ Generar fondo con IA';
    // Si la llamada fallo, la propuesta anterior sigue en pantalla: su boton vuelve a servir.
    const $otraDespues = document.getElementById('nx-otra-propuesta-btn');
    if ($otraDespues) { $otraDespues.disabled = false; $otraDespues.textContent = '🔄 Otra propuesta'; }
  }
}

function bindDelegation(container) {
  // Abortar listeners previos si existian (fix bug de 3 subs duplicadas).
  // Cada re-render del detalle llamaba esta funcion sin remover los listeners anteriores,
  // acumulando handlers de submit que disparaban multiples creaciones por 1 click.
  if (delegationAbortCtrl) {
    try { delegationAbortCtrl.abort(); } catch (_) {}
  }
  delegationAbortCtrl = new AbortController();
  const signal = delegationAbortCtrl.signal;

  container.addEventListener('click', async (e) => {
    const tabBtn = e.target.closest('.detail-tab');
    if (tabBtn) { switchTab(tabBtn.dataset.tab); return; }

    if (e.target.id === 'nx-biz-edit') {
      const $panel = document.getElementById('nx-biz-panel');
      const editableTabs = ['info', 'contacto', 'propietario', 'apariencia'];
      if ($panel && editableTabs.includes(currentTab)) {
        $panel.innerHTML = buildPanel(currentTab, currentBiz, true);
        if (currentTab !== 'apariencia') bindDeptChangeListeners($panel);
      }
      return;
    }

    if (e.target.id === 'nx-generar-fondo-btn' || e.target.id === 'nx-otra-propuesta-btn') {
      await handleGenerarFondo();
      return;
    }

    if (e.target.closest('#nx-fondo-restore-cache')) {
      const entry = await loadFondoWebhookPayload(businessId);
      if (!entry?.payload) {
        showStatus('No hay generación guardada en este dispositivo.', true);
        return;
      }
      const inj = injectFondoWebhookResult(entry.payload);
      if (!inj.ok) showStatus(inj.error || 'No se pudo mostrar la copia guardada.', true);
      else showStatus('Variantes recuperadas desde la copia local de este dispositivo.');
      return;
    }

    if (e.target.closest('#nx-fondo-discard-cache')) {
      await clearFondoWebhookPayload(businessId);
      const hint = document.getElementById('nx-fondo-cache-hint');
      if (hint) hint.hidden = true;
      showStatus('Copia local de la generación eliminada.');
      return;
    }

    // Botón "Ampliar": abre la imagen en pantalla completa / nueva pestaña
    const viewBtn = e.target.closest('.nx-fondo-view-btn');
    if (viewBtn) {
      const src   = resolveFondoSrcFromBtn(viewBtn);
      const isB64 = viewBtn.dataset.isB64 === 'true';
      if (!src) return;
      if (isB64) {
        // Convertir data URL a blob URL para que el navegador la abra sin restricciones
        try {
          const [header, b64data] = src.split(',');
          const mime   = header.split(':')[1].split(';')[0];
          const binary = atob(b64data);
          const bytes  = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          const blobUrl = URL.createObjectURL(new Blob([bytes], { type: mime }));
          const win = window.open(blobUrl, '_blank');
          if (win) setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        } catch {
          window.open(src, '_blank');
        }
      } else {
        window.open(src, '_blank');
      }
      return;
    }

    // Botón "Descargar": guarda el archivo en el disco local
    const dlBtn = e.target.closest('.nx-fondo-dl-btn');
    if (dlBtn) {
      const src      = resolveFondoSrcFromBtn(dlBtn);
      const isB64    = dlBtn.dataset.isB64 === 'true';
      const filename = `fondo_${dlBtn.dataset.label || 'negocio'}.${isB64 ? 'jpg' : 'png'}`;
      if (!src) return;
      if (isB64) {
        const a = document.createElement('a');
        a.href     = src;
        a.download = filename;
        a.click();
      } else {
        // URL remota (DALL-E): fetch → blob → descarga
        const origText      = dlBtn.textContent;
        dlBtn.disabled      = true;
        dlBtn.textContent   = '⏳';
        try {
          const blob     = await blobDeUrl(src);
          if (blob) {
            const blobUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href     = blobUrl;
            a.download = filename;
            a.click();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
          } else {
            window.open(src, '_blank');
          }
        } catch {
          window.open(src, '_blank');
        } finally {
          dlBtn.disabled    = false;
          dlBtn.textContent = origText;
        }
      }
      return;
    }

    const saveBtn = e.target.closest('.nx-fondo-save-btn');
    if (saveBtn) {
      const fondoSrc = resolveFondoSrcFromBtn(saveBtn);
      if (!fondoSrc) return;
      saveBtn.disabled    = true;

      // FONDO-PANEL-T1 paso 2 (12-oct): la ficha guarda la URL del archivo, nunca el base64
      // (FONDO-VPS-T1). Y SIN el webhook de mejorar calidad: la tarea lo retira (punto 3) porque el
      // flujo v2 entrega la imagen lista. El PR 402 lo habia dejado; se sube la propuesta tal cual.
      saveBtn.textContent = '⏳ Subiendo el fondo…';
      const elegido = fondoParaGuardar([fondoSrc]);
      let fondoUrl = elegido.url || null;
      if (elegido.error) {
        showStatus(`No se guardó el fondo: ${elegido.error}`, true);
        saveBtn.disabled    = false;
        saveBtn.textContent = '💾 Usar este fondo';
        return;
      }
      if (!fondoUrl) {
        const ext = elegido.mime === 'image/png' ? 'png' : 'jpg';
        const archivo = new File([elegido.bytes], `fondo.${ext}`, { type: elegido.mime });
        const subida = await AparienciaApiClient.subirArchivo(businessId, 'fondo', archivo, elegido.bytes.subarray(0, 8));
        if (!subida.ok) {
          showStatus(`No se guardó el fondo: ${subida.error}`, true);
          saveBtn.disabled    = false;
          saveBtn.textContent = '💾 Usar este fondo';
          return;
        }
        fondoUrl = subida.url;
      }

      saveBtn.textContent = '⏳ Guardando…';
      const result = await BusinessApiClient.updateAccountConfig(businessId, { fondoUrl });
      if (result.ok) {
        await clearFondoWebhookPayload(businessId);
        currentBiz.accountConfig = currentBiz.accountConfig || {};
        currentBiz.accountConfig.fondo_url = fondoUrl;
        showStatus('✅ Fondo guardado en el perfil del negocio.');
        document.querySelectorAll('.nx-fondo-save-btn').forEach(b => {
          b.textContent = b === saveBtn ? '✅ Guardado' : '💾 Usar este fondo';
          b.disabled = false;
        });
        const $panel = document.getElementById('nx-biz-panel');
        if ($panel && currentTab === 'apariencia') {
          $panel.innerHTML = buildPanel('apariencia', currentBiz, false);
          refreshAparienciaFondoCacheHint();
        }
      } else {
        showStatus(`Error al guardar fondo: ${result.error}`, true);
        saveBtn.disabled    = false;
        saveBtn.textContent = '💾 Usar este fondo';
      }
      return;
    }

    if (e.target.id === 'nx-biz-cancel' || e.target.id === 'nx-apariencia-cancel') {
      const $panel = document.getElementById('nx-biz-panel');
      if ($panel) $panel.innerHTML = buildPanel(currentTab, currentBiz, false);
      if (currentTab === 'apariencia') refreshAparienciaFondoCacheHint();
      if (currentTab === 'info') loadMarca();
      return;
    }

    if (e.target.id === 'nx-biz-delete') {
      if (!await confirmar({ titulo: `Eliminar "${currentBiz.displayName}"`, texto: 'Esta acción no se puede deshacer.', aceptar: 'Eliminar negocio', peligro: true })) return;
      e.target.disabled = true;
      const result = await BusinessApiClient.remove(businessId);
      if (result.ok) {
        window.location.hash = '#/negocios';
      } else {
        // NEGOCIOS-ARCHIVAR-API-T1: con ventas, equipos o pagos la API ya no borra (409 NEGOCIO_CON_HISTORIA).
        const r = leerRespuestaDeArchivo(result);
        showStatus(r.conHistoria ? `${r.mensaje} Usa "Archivar".` : `Error: ${r.error || result.error}`, true);
        e.target.disabled = false;
      }
      return;
    }

    // NEGOCIOS-ARCHIVAR-VISTA-T1: archivar con motivo; si la suscripcion sigue vigente la API pide confirmarlo.
    if (e.target.id === 'nx-biz-archivar') {
      e.target.disabled = true;
      let cancelaSuscripcion = false;
      let aviso = '';
      for (;;) {
        const motivo = await pedirMotivoDeArchivo(currentBiz.displayName, aviso, cancelaSuscripcion);
        if (motivo === null) break;
        const c = cuerpoDeArchivar(motivo, cancelaSuscripcion);
        if (!c.ok) { aviso = c.error; continue; }
        const r = leerRespuestaDeArchivo(await BusinessApiClient.archivar(businessId, c.cuerpo));
        if (r.hecho) { window.location.hash = '#/negocios'; return; }
        if (r.pideConfirmar && !cancelaSuscripcion) { cancelaSuscripcion = true; aviso = `${r.mensaje} Si de todas formas se archiva, su suscripcion se cancela.`; continue; }
        showStatus(r.mensaje || `Error: ${r.error}`, true);
        break;
      }
      e.target.disabled = false;
      return;
    }

    if (e.target.id === 'nx-biz-desarchivar') {
      e.target.disabled = true;
      const r = leerRespuestaDeArchivo(await BusinessApiClient.desarchivar(businessId));
      if (r.hecho) { window.location.reload(); return; }
      showStatus(r.mensaje || `Error: ${r.error}`, true);
      e.target.disabled = false;
      return;
    }

    const removeBtn = e.target.closest('[data-remove-user]');
    if (removeBtn) {
      const uid = removeBtn.dataset.removeUser;
      if (!await confirmar({ titulo: 'Quitar el acceso de este usuario', texto: `Usuario: ${uid}`, aceptar: 'Quitar acceso', peligro: true })) return;
      removeBtn.disabled = true;
      const result = await BusinessApiClient.removeUser(businessId, uid);
      if (result.ok) { await loadEquipos(); showStatus('Usuario removido'); }
      else { showStatus(`Error: ${result.error}`, true); removeBtn.disabled = false; }
      return;
    }

    if (e.target.id === 'nx-biz-add-user') {
      const uid = document.getElementById('nx-biz-add-uid')?.value?.trim();
      if (!uid) return;
      e.target.disabled = true;
      const result = await BusinessApiClient.addUser(businessId, uid);
      if (result.ok) {
        document.getElementById('nx-biz-add-uid').value = '';
        await loadEquipos();
        showStatus('Usuario agregado');
      } else {
        showStatus(`Error: ${result.error}`, true);
      }
      e.target.disabled = false;
      return;
    }

    if (e.target.id === 'nx-sub-new-btn') {
      const $form = document.getElementById('nx-sub-new-form');
      if ($form) {
        $form.hidden = !$form.hidden;
        // Default fechas al abrir: inicio = hoy, fin = ultimo dia del mes actual.
        // Evita que el usuario cree sub sin fecha limite (bug reportado).
        if (!$form.hidden) {
          const today = new Date();
          const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          const iso = (d) => d.toISOString().split('T')[0];
          const $start = document.getElementById('sub-start');
          const $end = document.getElementById('sub-end');
          if ($start && !$start.value) $start.value = iso(today);
          if ($end && !$end.value) $end.value = iso(lastDay);
        }
      }
      return;
    }

    if (e.target.id === 'nx-sub-cancel-btn') {
      const $form = document.getElementById('nx-sub-new-form');
      if ($form) $form.hidden = true;
      return;
    }

    const delSub = e.target.closest('[data-del-sub]');
    if (delSub) {
      const sid = delSub.dataset.delSub;
      if (!await confirmar({ titulo: 'Eliminar esta suscripción', aceptar: 'Eliminar suscripción', peligro: true })) return;
      delSub.disabled = true;
      const result = await SubscriptionApiClient.remove(businessId, sid);
      if (result.ok) { await loadSuscripciones(); showStatus('Suscripción eliminada'); }
      else { showStatus(`Error: ${result.error}`, true); delSub.disabled = false; }
      return;
    }

    const editSubBtn = e.target.closest('[data-edit-sub]');
    if (editSubBtn) {
      const sid = editSubBtn.dataset.editSub;
      const panel = document.getElementById(`sub-edit-${sid}`);
      if (panel) panel.hidden = !panel.hidden;
      return;
    }

    const cancelSubEdit = e.target.closest('[data-cancel-sub-edit]');
    if (cancelSubEdit) {
      const sid = cancelSubEdit.dataset.cancelSubEdit;
      const panel = document.getElementById(`sub-edit-${sid}`);
      if (panel) panel.hidden = true;
      return;
    }
  }, { signal });

  // 'load' no burbujea: se escucha en captura para leer el tamano real de cada propuesta.
  container.addEventListener('load', (e) => {
    if (e.target?.matches?.('[data-fondo-img]')) actualizarResumenDelSalon(e.target.closest('[data-fondo-card]'));
  }, { capture: true, signal });

  // EL EXAMINAR DEL LOGO (FONDO-PANEL-T1 paso 1, COORD #16139). Hasta hoy la ficha solo
  // tenia el campo URL: para poner el logo habia que subirlo a Dropbox y pegar el enlace.
  // Ahora el archivo sube por el endpoint de apariencia y lo que se guarda en la ficha es
  // la URL que devuelve el API -- nunca el archivo en base64.
  container.addEventListener('change', async (e) => {
    // FONDO-PANEL-T1 punto 2: el marco del salon se apaga para ver la imagen limpia, o cambia de
    // pantalla (restaurante / mostrador). Se repinta solo el marco de esa tarjeta.
    const $card = e.target.closest?.('[data-fondo-card]');
    if ($card && (e.target.classList.contains('nx-fondo-marco-toggle') || e.target.classList.contains('nx-fondo-marco-modo'))) {
      const modo = $card.querySelector('.nx-fondo-marco-modo')?.value || 'restaurante';
      const visible = $card.querySelector('.nx-fondo-marco-toggle')?.checked !== false;
      const $viejo = $card.querySelector('[data-fondo-marco]');
      if ($viejo) $viejo.outerHTML = piezasDelSalonHTML(modo);
      const $nuevo = $card.querySelector('[data-fondo-marco]');
      if ($nuevo) $nuevo.style.display = visible ? '' : 'none';
      actualizarResumenDelSalon($card);
      return;
    }
    if (e.target.id === 'ap-fondo-archivo') { await handleSubirFondo(e.target); return; }
    if (e.target.id !== 'ap-logo-archivo') return;
    await handleSubirLogo(e.target);
  }, { signal });

  container.addEventListener('submit', async (e) => {
    if (e.target.id === 'nx-sub-form') {
      e.preventDefault();
      const $err = document.getElementById('nx-sub-form-err');
      const $btn = e.target.querySelector('[type=submit]');
      if ($btn) $btn.disabled = true;
      if ($err) $err.hidden = true;

      const plan     = document.getElementById('sub-plan')?.value?.trim();
      const status   = document.getElementById('sub-status')?.value;
      const billing  = document.getElementById('sub-billing')?.value;
      const amount   = parseFloat(document.getElementById('sub-amount')?.value || '0') || 0;
      const currency = document.getElementById('sub-currency')?.value?.trim().toUpperCase() || 'COP';
      const start    = document.getElementById('sub-start')?.value || null;
      const end      = document.getElementById('sub-end')?.value   || null;
      const notes    = document.getElementById('sub-notes')?.value?.trim() || null;

      if (!plan) {
        if ($err) { $err.textContent = 'El plan es requerido'; $err.hidden = false; }
        if ($btn) $btn.disabled = false;
        return;
      }

      // PANEL-WIZARD-SUBS-FECHA-PASADA-001: rechazar vencimiento pasado antes de persistir.
      const fechaErr = validateSubDates(start, end);
      if (fechaErr) {
        if ($err) { $err.textContent = fechaErr; $err.hidden = false; }
        if ($btn) $btn.disabled = false;
        return;
      }

      const result = await SubscriptionApiClient.create(businessId, {
        plan, status, billingCycle: billing, amount, currency,
        startDate: start || undefined, endDate: end || undefined, notes
      });

      if (result.ok) {
        document.getElementById('nx-sub-new-form').hidden = true;
        e.target.reset();
        await loadSuscripciones();
        showStatus('Suscripción creada');
      } else {
        if ($err) { $err.textContent = result.error; $err.hidden = false; }
        if ($btn) $btn.disabled = false;
      }
      return;
    }

    const subEditForm = e.target.closest('[data-sub-edit-form]');
    if (subEditForm) {
      e.preventDefault();
      const sid = subEditForm.dataset.subEditForm;
      const fd = new FormData(subEditForm);
      const $btn = subEditForm.querySelector('[type=submit]');
      const $err = subEditForm.querySelector('.sub-edit-err');
      if ($btn) $btn.disabled = true;
      // PANEL-WIZARD-SUBS-FECHA-PASADA-001: rechazar vencimiento pasado antes de persistir.
      const editFechaErr = validateSubDates(fd.get('startDate'), fd.get('endDate'));
      if (editFechaErr) {
        if ($err) { $err.textContent = editFechaErr; $err.hidden = false; }
        if ($btn) $btn.disabled = false;
        return;
      }
      const payload = {
        plan:         fd.get('plan') || undefined,
        status:       fd.get('status') || undefined,
        billingCycle: fd.get('billingCycle') || undefined,
        amount:       parseFloat(fd.get('amount')) || 0,
        notes:        fd.get('notes') || null,
        startDate:    dateInputToISO(fd.get('startDate')),
        endDate:      dateInputToISO(fd.get('endDate')),
      };
      const res = await SubscriptionApiClient.update(businessId, sid, payload);
      if (res.ok) {
        document.getElementById(`sub-edit-${sid}`).hidden = true;
        await loadSuscripciones();
        showStatus('Suscripción actualizada');
      } else {
        if ($err) { $err.textContent = res.error; $err.hidden = false; }
        if ($btn) $btn.disabled = false;
      }
      return;
    }

    if (e.target.id === 'nx-apariencia-form') {
      e.preventDefault();
      if (isSaving) return;
      isSaving = true;
      const $btn = document.getElementById('nx-apariencia-save');
      const $err = document.getElementById('nx-apariencia-err');
      if ($btn) $btn.disabled = true;
      if ($err) $err.hidden = true;

      const val = (id) => document.getElementById(id)?.value?.trim() || null;
      const payload = {
        logoUrl:            val('ap-logoUrl'),
        fondoUrl:           val('ap-fondoUrl'),
        colorPrimario:      val('ap-colP') || null,
        colorSecundario:    val('ap-colS') || null,
        colorTexto:         val('ap-colT') || null,
        personajeUrl:       val('ap-personajeUrl'),
        personajeCategoria: val('ap-personajeCat'),
        fontName:           val('ap-fontName'),
      };
      // Eliminar nulls para hacer merge limpio
      Object.keys(payload).forEach(k => payload[k] === null && delete payload[k]);

      const result = await BusinessApiClient.updateAccountConfig(businessId, payload);
      if (result.ok) {
        // Actualizar accountConfig local para que el modo lectura lo refleje
        currentBiz.accountConfig = currentBiz.accountConfig || {};
        if (payload.logoUrl            !== undefined) currentBiz.accountConfig.logo_url             = payload.logoUrl;
        if (payload.fondoUrl           !== undefined) currentBiz.accountConfig.fondo_url            = payload.fondoUrl;
        if (payload.colorPrimario      !== undefined) currentBiz.accountConfig.color_primario       = payload.colorPrimario;
        if (payload.colorSecundario    !== undefined) currentBiz.accountConfig.color_secundario     = payload.colorSecundario;
        if (payload.colorTexto         !== undefined) currentBiz.accountConfig.color_texto          = payload.colorTexto;
        if (payload.personajeUrl       !== undefined) currentBiz.accountConfig.personaje_url        = payload.personajeUrl;
        if (payload.personajeCategoria !== undefined) currentBiz.accountConfig.personaje_categoria  = payload.personajeCategoria;
        if (payload.fontName           !== undefined) currentBiz.accountConfig.font_name            = payload.fontName;

        const $panel = document.getElementById('nx-biz-panel');
        if ($panel) $panel.innerHTML = buildAparienciaPanel(currentBiz, false);
        showStatus('Apariencia guardada');
        refreshAparienciaFondoCacheHint();
      } else {
        if ($err) { $err.textContent = result.error; $err.hidden = false; }
        if ($btn) $btn.disabled = false;
      }
      isSaving = false;
      return;
    }

    if (e.target.id !== 'nx-biz-form') return;
    e.preventDefault();
    if (isSaving) return;
    isSaving = true;

    const $saveBtn = document.getElementById('nx-biz-save');
    const $errMsg  = document.getElementById('nx-biz-save-err');
    if ($saveBtn) $saveBtn.disabled = true;
    if ($errMsg)  $errMsg.hidden = true;

    const payload = collectFormData();
    const result  = await BusinessApiClient.update(businessId, payload);

    if (result.ok) {
      currentBiz = result.data?.data || result.data || { ...currentBiz, ...payload };
      const $title = document.getElementById('nx-biz-title');
      if ($title) $title.textContent = currentBiz.displayName || '(sin nombre)';
      const $panel = document.getElementById('nx-biz-panel');
      if ($panel) $panel.innerHTML = buildPanel(currentTab, currentBiz, false);
      if (currentTab === 'info') loadMarca();
      showStatus('Cambios guardados');
    } else {
      if ($errMsg) { $errMsg.textContent = result.error; $errMsg.hidden = false; }
      if ($saveBtn) $saveBtn.disabled = false;
    }
    isSaving = false;
  }, { signal });
}
